/**
 * careerTransition.ts
 *
 * Career Transition (Product #2) — the orchestrator product.
 *
 * Positioning:
 *   Career Transition is the "quarterback" — the product that sequences the
 *   other CareerForge products into a coherent personalized journey and adds
 *   transition-specific content that no other product covers.
 *
 * Three audiences:
 *   - SWE → AI/ML Engineer
 *   - PM → AI PM
 *   - DA/DS → AI Engineer
 *
 * Six unique-content frameworks (not in any other CareerForge product):
 *   1. Readiness Assessment + Roadmap (AI-generated, rate-limited by tier)
 *   2. Transition Narrative Frameworks (how to explain a pivot)
 *   3. Financial Bridge Planning (runway math, bridge strategies)
 *   4. Pivoter Negotiation (different leverage than lateral moves)
 *   5. First 90 Days on an AI Team (onboarding playbook)
 *   6. Internal vs. External Transition Decision (framework)
 *
 * Tier gating:
 *   Free: 1-lifetime readiness assessment + roadmap + overview content
 *   Pro: 3/month roadmap regenerations + select framework previews
 *   All Access: Unlimited roadmap regens + all 6 frameworks + deep-dives
 */

import { PRICING, fmtUSD } from "../constants/pricing";
import { Localized, L } from "../utils/localized";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TierGate = "free" | "pro" | "all_access";

export type TransitionAudience = "swe_to_ai" | "pm_to_ai_pm" | "da_to_ai";

export interface AudienceProfile {
  key: TransitionAudience;
  label: string;
  shortLabel: string;
  currentRole: string;
  targetRole: string;
  color: string;
  positioning: string;
  biggestGap: string;
  biggestAsset: string;
  marketDemand: "very_high" | "high" | "moderate";
  typicalTimeline: string;
  compensationDelta: string; // honest range for typical transitioners
}

export const AUDIENCES: Localized<AudienceProfile[]> = {
  en: [
  {
    key: "swe_to_ai",
    label: "Software Engineer → AI/ML Engineer",
    shortLabel: "SWE → AI Eng",
    currentRole: "Software Engineer (5-15 yrs)",
    targetRole: "AI/ML Engineer · Agentic AI Engineer",
    color: "#2563eb",
    positioning:
      "You already build production systems. What's missing is the AI-specific stack: agentic patterns, evaluation rigor, prompt engineering as a discipline, and the 7-domain fluency hiring managers screen for.",
    biggestGap:
      "Agentic patterns (D2), evaluation methodology (D4), and prompt-engineering-as-engineering (D1). Many SWEs arrive thinking 'I can just call an LLM' and are surprised by the eval + safety layer.",
    biggestAsset:
      "Production instinct (D5/D7). SWE candidates don't need to learn shipping — they need to learn what's different about shipping LLM-powered systems. That's a 3-month journey, not a 12-month bootcamp.",
    marketDemand: "very_high",
    typicalTimeline: "4–6 months concentrated effort",
    compensationDelta: "Typically flat to +10% at comparable seniority; +20–30% if leveling up (senior → staff) during transition",
  },
  {
    key: "pm_to_ai_pm",
    label: "Product Manager → AI Product Manager",
    shortLabel: "PM → AI PM",
    currentRole: "Product Manager (5-15 yrs)",
    targetRole: "AI Product Manager · AI Platform PM",
    color: "#9333ea",
    positioning:
      "The gap isn't product sense — it's technical calibration. You need to know enough about prompts, evals, RAG, and latency/cost tradeoffs to lead technical teams credibly and size AI opportunities accurately.",
    biggestGap:
      "Technical calibration (D1, D3, D4). PMs who can't separate 'this LLM use case will work' from 'this one will hallucinate badly' get routed back to non-AI PM roles.",
    biggestAsset:
      "Customer-problem fluency + roadmap discipline (D7). AI PMs who can say 'the eval set showed 60% grounding — we don't ship until 85%' are immediately credible with engineering.",
    marketDemand: "very_high",
    typicalTimeline: "3–5 months concentrated effort",
    compensationDelta: "Typically +15–30% at comparable seniority — AI PM demand exceeds supply significantly",
  },
  {
    key: "da_to_ai",
    label: "Data Analyst / Data Scientist → AI Engineer",
    shortLabel: "DA/DS → AI Eng",
    currentRole: "Data Analyst or Data Scientist (3-10 yrs)",
    targetRole: "AI Engineer · ML Engineer (Applied)",
    color: "#0d9488",
    positioning:
      "You have statistics and modeling; what's missing is the engineering spine — shipping production systems, orchestration, observability, and agentic patterns. The pivot is from analysis to deployed, trafficked systems.",
    biggestGap:
      "Production engineering (D5). Many DA/DS candidates have never deployed code that other engineers depend on. Learning FastAPI, Docker, traces, and CI is the multi-month lift.",
    biggestAsset:
      "Evaluation instinct (D4). You already know what a good test set looks like — that's exactly the skill hiring managers cite as the rarest differentiator.",
    marketDemand: "high",
    typicalTimeline: "5–8 months concentrated effort",
    compensationDelta: "Typically +10–25% when transitioning from DA; +5–15% from DS (since DS salaries already premium)",
  },
  ],
  es: [
  {
    "key": "swe_to_ai",
    "label": "Software Engineer → AI/ML Engineer",
    "shortLabel": "SWE → AI Eng",
    "currentRole": "Software Engineer (5-15 años)",
    "targetRole": "AI/ML Engineer · Agentic AI Engineer",
    "color": "#2563eb",
    "positioning": "Ya construyes sistemas en producción. Lo que falta es el stack específico de IA: patrones agénticos, rigor en la evaluación, la ingeniería de prompts como disciplina y la fluidez en los 7 dominios que los hiring managers filtran.",
    "biggestGap": "Patrones agénticos (D2), metodología de evaluación (D4) e ingeniería-de-prompts-como-ingeniería (D1). Muchos SWEs llegan pensando 'solo tengo que llamar a un LLM' y se sorprenden con la capa de eval + seguridad.",
    "biggestAsset": "Instinto de producción (D5/D7). Los candidatos SWE no necesitan aprender a lanzar productos — necesitan aprender qué es diferente al lanzar sistemas impulsados por LLM. Es un recorrido de 3 meses, no un bootcamp de 12 meses.",
    "marketDemand": "very_high",
    "typicalTimeline": "4–6 meses de esfuerzo concentrado",
    "compensationDelta": "Normalmente sin cambios o hasta +10% en un nivel de seniority comparable; +20–30% si subes de nivel (senior → staff) durante la transición"
  },
  {
    "key": "pm_to_ai_pm",
    "label": "Product Manager → AI Product Manager",
    "shortLabel": "PM → AI PM",
    "currentRole": "Product Manager (5-15 años)",
    "targetRole": "AI Product Manager · AI Platform PM",
    "color": "#9333ea",
    "positioning": "La brecha no es el sentido de producto — es la calibración técnica. Necesitas saber lo suficiente sobre prompts, evals, RAG y los tradeoffs de latencia/costo para liderar equipos técnicos con credibilidad y dimensionar oportunidades de IA con precisión.",
    "biggestGap": "Calibración técnica (D1, D3, D4). Los PMs que no pueden distinguir 'este caso de uso de LLM va a funcionar' de 'este va a alucinar gravemente' terminan reenviados a roles de PM no relacionados con IA.",
    "biggestAsset": "Fluidez en el problema del cliente + disciplina de roadmap (D7). Los AI PMs que pueden decir 'el eval set mostró un 60% de grounding — no lanzamos hasta el 85%' obtienen credibilidad inmediata con ingeniería.",
    "marketDemand": "very_high",
    "typicalTimeline": "3–5 meses de esfuerzo concentrado",
    "compensationDelta": "Normalmente +15–30% en un nivel de seniority comparable — la demanda de AI PM supera significativamente la oferta"
  },
  {
    "key": "da_to_ai",
    "label": "Data Analyst / Data Scientist → AI Engineer",
    "shortLabel": "DA/DS → AI Eng",
    "currentRole": "Data Analyst o Data Scientist (3-10 años)",
    "targetRole": "AI Engineer · ML Engineer (Applied)",
    "color": "#0d9488",
    "positioning": "Tienes estadística y modelado; lo que falta es la columna vertebral de ingeniería — lanzar sistemas en producción, orquestación, observabilidad y patrones agénticos. El giro va del análisis a sistemas desplegados y con tráfico.",
    "biggestGap": "Ingeniería de producción (D5). Muchos candidatos DA/DS nunca han desplegado código del que dependan otros ingenieros. Aprender FastAPI, Docker, traces y CI es el esfuerzo de varios meses.",
    "biggestAsset": "Instinto de evaluación (D4). Ya sabes cómo se ve un buen test set — esa es justamente la habilidad que los hiring managers citan como el diferenciador más escaso.",
    "marketDemand": "high",
    "typicalTimeline": "5–8 meses de esfuerzo concentrado",
    "compensationDelta": "Normalmente +10–25% al hacer la transición desde DA; +5–15% desde DS (dado que los salarios de DS ya son premium)"
  }
],
};

export const getAudienceByKey = (
  key: TransitionAudience,
  locale: string = "en",
): AudienceProfile | undefined => L(AUDIENCES, locale).find((a) => a.key === key);

// ---------------------------------------------------------------------------
// Six unique-content frameworks
// ---------------------------------------------------------------------------

export interface FrameworkModule {
  slug: string;
  number: number;
  title: string;
  tagline: string;
  tier: TierGate;
  studyTime: string;
  whyItMatters: string;
  keyInsight: string;
  sectionsPreview: string[]; // for the card display
}

export const FRAMEWORKS: Localized<FrameworkModule[]> = {
  en: [
  {
    slug: "readiness-assessment",
    number: 1,
    title: "Readiness Assessment + AI Roadmap",
    tagline:
      "15-question diagnostic → personalized roadmap. Claude-powered. Routed into every other CareerForge product at the right moment.",
    tier: "free",
    studyTime: "10 min assessment · instant roadmap",
    whyItMatters:
      "Most career transitions fail because candidates pick the wrong thing to work on first. The roadmap sequences the work ahead around your specific gaps, timeline, and risk tolerance.",
    keyInsight:
      "A personalized roadmap beats any generic 'how to become an AI engineer' guide — not because the steps are secret, but because the right sequence for a SWE with an existing job is different from the right sequence for a PM between roles.",
    sectionsPreview: [
      "Current state: role, experience, skills self-rating across agentic domains",
      "Target state: role, seniority, timeline, risk tolerance",
      "Constraints: financial runway, time availability, geographic / visa",
      "Output: readiness score, archetype, personalized roadmap, priority risks",
    ],
  },
  {
    slug: "transition-narratives",
    number: 2,
    title: "Transition Narrative Frameworks",
    tagline:
      "How to answer 'Why are you making this change?' in interviews, on LinkedIn, in cover letters, and to your current manager — without sounding like you're running from something.",
    tier: "all_access",
    studyTime: "45 min",
    whyItMatters:
      "The #1 silent-killer of career transitions is a weak transition narrative. Hiring managers pattern-match on 'running away' vs 'running toward' in the first 30 seconds of a conversation.",
    keyInsight:
      "Strong transition narratives braid three threads: what you're keeping (continuity), what you're adding (growth), and what you're leaving behind (honest). Candidates who try to hide the 'leaving behind' part get caught in reference checks.",
    sectionsPreview: [
      "The 3-thread framework: Keep / Add / Leave behind",
      "7 narrative archetypes (expansion · return · repositioning · escape etc.)",
      "Channel-specific variants: interview / LinkedIn / resume cover / current manager",
      "The 'Why now?' follow-up — the question that unmasks weak narratives",
      "10 example narratives across the 3 audience types",
    ],
  },
  {
    slug: "financial-bridge",
    number: 3,
    title: "Financial Bridge Planning",
    tagline:
      "Runway math, income-during-transition strategies, and the 3 bridge archetypes. Concrete spreadsheets and decision triggers — not platitudes about 'saving up.'",
    tier: "all_access",
    studyTime: "60 min + worksheet",
    whyItMatters:
      "Most career transition content ignores the money. But financial pressure is what causes candidates to accept the first mediocre offer rather than wait for the right one — undoing months of preparation.",
    keyInsight:
      "Your minimum viable runway is NOT 'months of expenses saved.' It's 'months of expenses saved + expected-offer-delay + negotiation-leverage-buffer.' The last term is why people who quit with 6 months saved still accept bad offers after 3 months.",
    sectionsPreview: [
      "The 3 bridge archetypes: Job-Hold (stay employed, learn on side), Job-Bridge (exit into contract/consulting), Job-Quit (pure runway)",
      "Runway math worksheet (fixed costs · variable costs · leverage buffer)",
      "Tax-efficient bridge strategies (401k backdoor, HSA, LLC consulting)",
      "When to accept the first offer vs. wait — decision triggers",
      "Offer-timing asymmetry: why your first month of job-searching is worth 3x your fourth",
    ],
  },
  {
    slug: "pivoter-negotiation",
    number: 4,
    title: "Negotiation for Pivoters",
    tagline:
      "Why your negotiation leverage is different when you're pivoting roles, and how to use it. The 5 moves that work for transitioners and the 3 that destroy offers.",
    tier: "all_access",
    studyTime: "50 min",
    whyItMatters:
      "Lateral-move negotiation advice gets pivoters underpaid. When you're changing roles, your leverage sources are different — you're often the hiring team's 'risk hire' and both of you know it.",
    keyInsight:
      "Pivoters negotiate on scope and growth, not just comp. The single move that most improves pivoter outcomes: asking for a defined 6-month scope review tied to concrete deliverables. Makes the hiring manager comfortable taking the 'risk' while giving you a forcing function for comp adjustment.",
    sectionsPreview: [
      "Your actual leverage as a pivoter (spoiler: it's the ramp-up risk you're absorbing)",
      "The 5 pivoter-specific moves: scope-review trigger, signing-bonus equivalency, growth-path contract, reference-network protection, timeline flexibility",
      "The 3 destroyers: lowballing yourself, comparing to peers in your old role, accepting 'we need to see you perform'",
      "Scripts: opening, counter, silence, walk-away",
      "When to take less money (real circumstances where it's correct)",
    ],
  },
  {
    slug: "first-90-days",
    number: 5,
    title: "First 90 Days on an AI Team",
    tagline:
      "The onboarding playbook. What to learn, who to meet, what to ship, and what not to say in your first all-hands. The ramp strategy that preempts 'did we hire the right person?'",
    tier: "all_access",
    studyTime: "40 min",
    whyItMatters:
      "The hiring decision isn't final until day 90. AI teams especially are aggressive about managing out pivoters who don't ramp fast. Your first 90 days determine whether you're seen as the 'great hire' or 'mistake we have to work around.'",
    keyInsight:
      "The single highest-leverage first-90 move: ship a small, visible, *complete* win in week 3–5. Not your biggest project — your fastest complete loop. It reframes every conversation about your ramp from 'are they fast enough?' to 'what should we give them next?'",
    sectionsPreview: [
      "Week 1–2: The observation week (not the 'make your mark' week)",
      "Week 3–5: The first visible ship — picking the right small project",
      "Week 6–8: The domain owner conversation — how to claim scope without overreaching",
      "Week 9–12: The 90-day conversation — what to propose proactively",
      "The anti-patterns: criticizing existing code, volunteering for too much, hiding that you don't know something",
    ],
  },
  {
    slug: "internal-vs-external",
    number: 6,
    title: "Internal vs. External Transition Decision",
    tagline:
      "Should you transition within your current company or leave? The 12-factor decision framework — with 3 worked examples showing which factor dominates in each case.",
    tier: "all_access",
    studyTime: "35 min",
    whyItMatters:
      "Internal transitions look safer but often fail because the hiring manager can't see the 'new you' past the old one. External transitions look riskier but frequently result in better titles, comp, and scope. This framework makes the trade-off explicit.",
    keyInsight:
      "The hidden dominant factor is usually 'reference credibility.' Internal pivoters inherit the reputation of their old role; external pivoters get a fresh read. If your old reputation is strong in the target domain, internal wins. If it's strong in an unrelated domain, external wins.",
    sectionsPreview: [
      "The 12 factors: time-to-role · comp delta · role quality · reference credibility · political risk · learning velocity · mentor access · product quality · team strength · org AI maturity · exit optionality · psychological cost",
      "How to weight factors based on your specific audience (SWE/PM/DA)",
      "3 worked examples: SWE staying at Stripe · PM leaving Meta · DA staying at Stitch Fix",
      "The 'internal-first rule' (try internal, set a deadline, go external if not progressed)",
      "When NOT to try internal first (3 red flags)",
    ],
  },
  ],
  es: [
  {
    "slug": "readiness-assessment",
    "number": 1,
    "title": "Evaluación de preparación + hoja de ruta de IA",
    "tagline": "Diagnóstico de 15 preguntas → hoja de ruta personalizada. Impulsado por Claude. Te dirige a cada uno de los demás productos de CareerForge en el momento adecuado.",
    "tier": "free",
    "studyTime": "Evaluación de 10 min · hoja de ruta instantánea",
    "whyItMatters": "La mayoría de las transiciones profesionales fracasan porque los candidatos eligen mal en qué trabajar primero. La hoja de ruta secuencia el trabajo por delante en función de tus brechas específicas, tu cronograma y tu tolerancia al riesgo.",
    "keyInsight": "Una hoja de ruta personalizada supera a cualquier guía genérica de 'cómo convertirse en ingeniero de IA', no porque los pasos sean secretos, sino porque la secuencia correcta para un SWE con un empleo actual es distinta de la secuencia correcta para un PM entre puestos.",
    "sectionsPreview": [
      "Estado actual: rol, experiencia, autoevaluación de habilidades en dominios de agentes",
      "Estado objetivo: rol, nivel de seniority, cronograma, tolerancia al riesgo",
      "Restricciones: colchón financiero, disponibilidad de tiempo, factores geográficos / visado",
      "Resultado: puntuación de preparación, arquetipo, hoja de ruta personalizada, riesgos prioritarios"
    ]
  },
  {
    "slug": "transition-narratives",
    "number": 2,
    "title": "Marcos de narrativa de transición",
    "tagline": "Cómo responder '¿Por qué haces este cambio?' en entrevistas, en LinkedIn, en cartas de presentación y ante tu gerente actual, sin que parezca que estás huyendo de algo.",
    "tier": "all_access",
    "studyTime": "45 min",
    "whyItMatters": "El asesino silencioso número 1 de las transiciones profesionales es una narrativa de transición débil. Los gerentes de contratación identifican patrones de 'huir de' frente a 'avanzar hacia' en los primeros 30 segundos de una conversación.",
    "keyInsight": "Las narrativas de transición sólidas entrelazan tres hilos: lo que conservas (continuidad), lo que agregas (crecimiento) y lo que dejas atrás (honestidad). Los candidatos que intentan ocultar la parte de 'dejar atrás' quedan al descubierto en las verificaciones de referencias.",
    "sectionsPreview": [
      "El marco de 3 hilos: Conservar / Agregar / Dejar atrás",
      "7 arquetipos narrativos (expansión · regreso · reposicionamiento · escape, etc.)",
      "Variantes específicas por canal: entrevista / LinkedIn / carta del currículum / gerente actual",
      "El seguimiento de '¿Por qué ahora?': la pregunta que desenmascara las narrativas débiles",
      "10 narrativas de ejemplo en los 3 tipos de audiencia"
    ]
  },
  {
    "slug": "financial-bridge",
    "number": 3,
    "title": "Planificación del puente financiero",
    "tagline": "Cálculo del colchón económico, estrategias de ingresos durante la transición y los 3 arquetipos de puente. Hojas de cálculo concretas y desencadenantes de decisión, no lugares comunes sobre 'ahorrar'.",
    "tier": "all_access",
    "studyTime": "60 min + hoja de trabajo",
    "whyItMatters": "La mayoría del contenido sobre transición profesional ignora el dinero. Pero la presión financiera es lo que lleva a los candidatos a aceptar la primera oferta mediocre en lugar de esperar la adecuada, echando por tierra meses de preparación.",
    "keyInsight": "Tu colchón mínimo viable NO es 'meses de gastos ahorrados'. Es 'meses de gastos ahorrados + retraso esperado de la oferta + margen para tener poder de negociación'. El último término explica por qué quienes renuncian con 6 meses ahorrados igual aceptan malas ofertas tras 3 meses.",
    "sectionsPreview": [
      "Los 3 arquetipos de puente: Mantener el empleo (sigues empleado, aprendes por tu cuenta), Puente laboral (sales hacia contrato/consultoría), Renuncia total (colchón puro)",
      "Hoja de trabajo del cálculo del colchón (costos fijos · costos variables · margen de poder de negociación)",
      "Estrategias de puente fiscalmente eficientes (401k backdoor, HSA, consultoría con LLC)",
      "Cuándo aceptar la primera oferta frente a esperar: desencadenantes de decisión",
      "Asimetría del momento de la oferta: por qué tu primer mes de búsqueda de empleo vale 3 veces más que el cuarto"
    ]
  },
  {
    "slug": "pivoter-negotiation",
    "number": 4,
    "title": "Negociación para quienes cambian de rumbo",
    "tagline": "Por qué tu poder de negociación es distinto cuando cambias de rol y cómo aprovecharlo. Las 5 movidas que funcionan para quienes hacen transiciones y las 3 que destruyen ofertas.",
    "tier": "all_access",
    "studyTime": "50 min",
    "whyItMatters": "El consejo de negociación para movimientos laterales deja mal pagados a quienes cambian de rumbo. Cuando cambias de rol, tus fuentes de poder son distintas: a menudo eres la 'contratación de riesgo' del equipo de contratación y ambos lo saben.",
    "keyInsight": "Quienes cambian de rumbo negocian sobre alcance y crecimiento, no solo sobre la compensación. La única movida que más mejora los resultados: pedir una revisión de alcance definida a los 6 meses vinculada a entregables concretos. Hace que el gerente de contratación se sienta cómodo asumiendo el 'riesgo' y te da un mecanismo forzoso para ajustar la compensación.",
    "sectionsPreview": [
      "Tu verdadero poder como quien cambia de rumbo (spoiler: es el riesgo de puesta al día que estás absorbiendo)",
      "Las 5 movidas específicas para quienes cambian de rumbo: desencadenante de revisión de alcance, equivalencia de bono por firma, contrato de trayectoria de crecimiento, protección de la red de referencias, flexibilidad de cronograma",
      "Los 3 destructores: subvalorarte, compararte con colegas de tu antiguo rol, aceptar 'necesitamos verte rendir'",
      "Guiones: apertura, contraoferta, silencio, retirada",
      "Cuándo aceptar menos dinero (circunstancias reales en las que es lo correcto)"
    ]
  },
  {
    "slug": "first-90-days",
    "number": 5,
    "title": "Los primeros 90 días en un equipo de IA",
    "tagline": "El manual de incorporación. Qué aprender, a quién conocer, qué entregar y qué no decir en tu primera reunión general. La estrategia de puesta al día que se anticipa al '¿contratamos a la persona adecuada?'",
    "tier": "all_access",
    "studyTime": "40 min",
    "whyItMatters": "La decisión de contratación no es definitiva hasta el día 90. Los equipos de IA en particular son agresivos al momento de desvincular a quienes cambian de rumbo y no se ponen al día rápido. Tus primeros 90 días determinan si te ven como la 'gran contratación' o el 'error que hay que sortear'.",
    "keyInsight": "La movida de mayor impacto en los primeros 90 días: entregar una victoria pequeña, visible y *completa* en las semanas 3 a 5. No tu proyecto más grande, sino tu ciclo completo más rápido. Reformula toda conversación sobre tu puesta al día de '¿es lo suficientemente rápido?' a '¿qué deberíamos darle a continuación?'",
    "sectionsPreview": [
      "Semanas 1 a 2: La semana de observación (no la semana de 'dejar tu huella')",
      "Semanas 3 a 5: La primera entrega visible: elegir el proyecto pequeño adecuado",
      "Semanas 6 a 8: La conversación de dueño del dominio: cómo reclamar alcance sin extralimitarte",
      "Semanas 9 a 12: La conversación de los 90 días: qué proponer de forma proactiva",
      "Los antipatrones: criticar el código existente, ofrecerte para demasiado, ocultar que no sabes algo"
    ]
  },
  {
    "slug": "internal-vs-external",
    "number": 6,
    "title": "Decisión de transición interna vs. externa",
    "tagline": "¿Deberías hacer la transición dentro de tu empresa actual o irte? El marco de decisión de 12 factores, con 3 ejemplos resueltos que muestran qué factor domina en cada caso.",
    "tier": "all_access",
    "studyTime": "35 min",
    "whyItMatters": "Las transiciones internas parecen más seguras, pero a menudo fracasan porque el gerente de contratación no puede ver a la 'nueva versión de ti' más allá de la anterior. Las transiciones externas parecen más arriesgadas, pero con frecuencia resultan en mejores títulos, compensación y alcance. Este marco hace explícita esa disyuntiva.",
    "keyInsight": "El factor dominante oculto suele ser la 'credibilidad de las referencias'. Quienes cambian de rumbo internamente heredan la reputación de su antiguo rol; quienes lo hacen externamente obtienen una lectura fresca. Si tu antigua reputación es sólida en el dominio objetivo, gana lo interno. Si es sólida en un dominio no relacionado, gana lo externo.",
    "sectionsPreview": [
      "Los 12 factores: tiempo hasta el rol · diferencia de compensación · calidad del rol · credibilidad de las referencias · riesgo político · velocidad de aprendizaje · acceso a mentores · calidad del producto · fortaleza del equipo · madurez de IA de la organización · opcionalidad de salida · costo psicológico",
      "Cómo ponderar los factores según tu audiencia específica (SWE/PM/DA)",
      "3 ejemplos resueltos: SWE que se queda en Stripe · PM que deja Meta · DA que se queda en Stitch Fix",
      "La 'regla de intentar primero lo interno' (prueba internamente, fija un plazo, ve por lo externo si no hay avances)",
      "Cuándo NO intentar primero lo interno (3 señales de alerta)"
    ]
  }
],
};

export const getFrameworkBySlug = (
  slug: string,
  locale: string = "en",
): FrameworkModule | undefined => L(FRAMEWORKS, locale).find((f) => f.slug === slug);

// ---------------------------------------------------------------------------
// Readiness Assessment Questions (15 questions across 4 categories)
// ---------------------------------------------------------------------------

export type QuestionType = "select" | "multi_select" | "text" | "slider" | "skill_matrix";

export interface AssessmentQuestion {
  id: string;
  category: "current_state" | "target_state" | "constraints" | "preferences";
  type: QuestionType;
  prompt: string;
  helpText?: string;
  required: boolean;
  options?: { value: string; label: string }[];
  skillRows?: { key: string; label: string; definition: string }[]; // for skill_matrix
  scaleMin?: number; // for slider
  scaleMax?: number;
  scaleLabels?: [string, string]; // [min label, max label]
  placeholder?: string;
  dependsOn?: { questionId: string; value: string }; // conditional question display
}

export const ASSESSMENT_QUESTIONS: Localized<AssessmentQuestion[]> = {
  en: [
  // Current State (4 questions)
  {
    id: "q1_current_role",
    category: "current_state",
    type: "select",
    prompt: "What's your current role?",
    required: true,
    options: [
      { value: "swe", label: "Software Engineer" },
      { value: "senior_swe", label: "Senior/Staff Software Engineer" },
      { value: "pm", label: "Product Manager" },
      { value: "senior_pm", label: "Senior/Group/Director PM" },
      { value: "da", label: "Data Analyst" },
      { value: "ds", label: "Data Scientist" },
      { value: "em", label: "Engineering Manager" },
      { value: "designer", label: "Product Designer" },
      { value: "other", label: "Other" },
    ],
  },
  {
    id: "q2_years_experience",
    category: "current_state",
    type: "select",
    prompt: "How many years of professional experience?",
    required: true,
    options: [
      { value: "0-2", label: "0–2 years" },
      { value: "3-5", label: "3–5 years" },
      { value: "6-10", label: "6–10 years" },
      { value: "11-15", label: "11–15 years" },
      { value: "16+", label: "16+ years" },
    ],
  },
  {
    id: "q3_ai_exposure",
    category: "current_state",
    type: "select",
    prompt: "How much hands-on AI/LLM work have you done in your current role?",
    helpText: "Not courses — shipping or building with AI in a real context.",
    required: true,
    options: [
      { value: "none", label: "None / very little" },
      { value: "side", label: "Side projects only" },
      { value: "partial", label: "Some at work — tools or small features" },
      { value: "significant", label: "Significant — owned AI features in production" },
      { value: "leading", label: "Leading AI initiatives already" },
    ],
  },
  {
    id: "q4_skill_matrix",
    category: "current_state",
    type: "skill_matrix",
    prompt: "Rate yourself on each domain (0 = no exposure, 5 = could teach it)",
    required: true,
    skillRows: [
      { key: "d1", label: "Prompting & Context Engineering", definition: "System prompts, few-shot, prompt injection defenses, context window management" },
      { key: "d2", label: "Agentic Patterns", definition: "ReAct, planner-executor, multi-agent, HITL — when to use which" },
      { key: "d3", label: "Tool Use & Integration", definition: "Function calling, MCP, tool schemas, API orchestration" },
      { key: "d4", label: "Evaluation & Reliability", definition: "Eval sets, grounding checks, adversarial testing, Ragas/LangSmith" },
      { key: "d5", label: "Systems Design (AI-specific)", definition: "RAG architectures, vector stores, cost/latency tradeoffs, deployment" },
      { key: "d6", label: "Safety & Governance", definition: "Prompt injection, refusal patterns, HITL gates, audit logs" },
      { key: "d7", label: "Product Thinking (AI)", definition: "What makes an AI feature ship-worthy, dealing with non-determinism, UX of uncertainty" },
    ],
  },

  // Target State (4 questions)
  {
    id: "q5_target_role",
    category: "target_state",
    type: "select",
    prompt: "What role are you targeting?",
    required: true,
    options: [
      { value: "ai_eng", label: "AI/ML Engineer" },
      { value: "agentic_eng", label: "Agentic AI Engineer" },
      { value: "ai_pm", label: "AI Product Manager" },
      { value: "ai_platform_pm", label: "AI Platform PM" },
      { value: "applied_ml", label: "Applied ML Engineer" },
      { value: "ai_research_eng", label: "AI Research Engineer" },
      { value: "exploring", label: "Still exploring — want the roadmap to help me decide" },
    ],
  },
  {
    id: "q6_target_seniority",
    category: "target_state",
    type: "select",
    prompt: "What seniority band are you targeting?",
    required: true,
    options: [
      { value: "mid", label: "Mid-level (IC3/IC4 equivalent)" },
      { value: "senior", label: "Senior (IC5 equivalent)" },
      { value: "staff", label: "Staff (IC6 equivalent)" },
      { value: "principal", label: "Principal+ (IC7+ equivalent)" },
      { value: "flexible", label: "Flexible — whatever the role calls for" },
    ],
  },
  {
    id: "q7_timeline",
    category: "target_state",
    type: "select",
    prompt: "Target transition timeline",
    required: true,
    options: [
      { value: "3mo", label: "3 months (aggressive — you're already close)" },
      { value: "6mo", label: "6 months (standard)" },
      { value: "12mo", label: "12 months (thorough — building depth)" },
      { value: "18mo+", label: "18+ months (major rebuild)" },
    ],
  },
  {
    id: "q8_target_companies",
    category: "target_state",
    type: "multi_select",
    prompt: "What company types are you targeting? (pick all that apply)",
    required: true,
    options: [
      { value: "frontier_labs", label: "Frontier AI labs (Anthropic, OpenAI, DeepMind)" },
      { value: "faang", label: "FAANG with significant AI investment" },
      { value: "ai_startup", label: "AI-native startup (seed–Series B)" },
      { value: "ai_scale", label: "AI-native scale-up (Series C+)" },
      { value: "traditional", label: "Traditional company adding AI (fintech, health, enterprise)" },
      { value: "consulting", label: "Consulting / agency" },
    ],
  },

  // Constraints (4 questions)
  {
    id: "q9_current_role_status",
    category: "constraints",
    type: "select",
    prompt: "What's your current employment situation?",
    required: true,
    options: [
      { value: "employed_stable", label: "Employed, stable — transitioning deliberately" },
      { value: "employed_unstable", label: "Employed but at-risk (layoffs, performance, org)" },
      { value: "between", label: "Between roles currently" },
      { value: "consulting", label: "Consulting / freelance" },
      { value: "sabbatical", label: "On intentional break / sabbatical" },
    ],
  },
  {
    id: "q10_time_availability",
    category: "constraints",
    type: "select",
    prompt: "How much time per week can you dedicate to the transition?",
    helpText: "Realistic, not aspirational. Plans that assume 20 hrs/week when you have 5 fail.",
    required: true,
    options: [
      { value: "5-10", label: "5–10 hrs/week (evenings only)" },
      { value: "10-15", label: "10–15 hrs/week (most common)" },
      { value: "15-20", label: "15–20 hrs/week (most common)" },
      { value: "20-30", label: "20–30 hrs/week (significant commitment)" },
      { value: "40+", label: "40+ hrs/week (full-time on transition)" },
    ],
  },
  {
    id: "q11_runway",
    category: "constraints",
    type: "select",
    prompt: "If you quit today, how many months of financial runway do you have?",
    helpText: "Rough — we use this to calibrate job-hold vs. job-bridge vs. job-quit strategies.",
    required: true,
    options: [
      { value: "<3", label: "< 3 months" },
      { value: "3-6", label: "3–6 months" },
      { value: "6-12", label: "6–12 months" },
      { value: "12-24", label: "12–24 months" },
      { value: "24+", label: "24+ months / no runway constraint" },
    ],
  },
  {
    id: "q12_geographic",
    category: "constraints",
    type: "select",
    prompt: "Geographic / work-location constraints",
    required: true,
    options: [
      { value: "remote_only", label: "Remote-only (fully distributed)" },
      { value: "hybrid_us", label: "Hybrid US (SF / NYC / Seattle / Austin OK)" },
      { value: "hybrid_eu", label: "Hybrid EU (London / Berlin / Paris)" },
      { value: "local_only", label: "Local-only (specific metro)" },
      { value: "flexible", label: "Flexible including relocation" },
      { value: "visa_dependent", label: "Visa-dependent (sponsorship matters)" },
    ],
  },

  // Preferences & risk (3 questions)
  {
    id: "q13_risk_tolerance",
    category: "preferences",
    type: "slider",
    prompt: "Risk tolerance",
    helpText: "1 = prefer internal / low risk · 5 = quit and go full external / high risk",
    required: true,
    scaleMin: 1,
    scaleMax: 5,
    scaleLabels: ["Low risk — internal preferred", "High risk — bold move OK"],
  },
  {
    id: "q14_portfolio_status",
    category: "preferences",
    type: "select",
    prompt: "What's your current portfolio / project status?",
    required: true,
    options: [
      { value: "none", label: "No AI-specific public work yet" },
      { value: "starting", label: "Some small tutorials / hello-world projects" },
      { value: "in_progress", label: "1–2 in-progress projects, not yet shipped" },
      { value: "shipped_1", label: "1 solid shipped project" },
      { value: "shipped_multi", label: "Multiple shipped projects with traces + evals" },
    ],
  },
  {
    id: "q15_biggest_worry",
    category: "preferences",
    type: "select",
    prompt: "What's your biggest worry about this transition?",
    required: true,
    options: [
      { value: "fundamentals", label: "I don't have the fundamentals" },
      { value: "portfolio", label: "I don't have shipped evidence" },
      { value: "interviews", label: "I'll fail the interviews even if I have the skills" },
      { value: "narrative", label: "I won't be able to explain the pivot credibly" },
      { value: "finances", label: "Money — can't afford a gap or take the wrong offer" },
      { value: "age_bias", label: "Age / level bias in AI hiring" },
      { value: "analysis_paralysis", label: "I keep restarting the plan and not shipping" },
    ],
  },
  ],
  es: [
  {
    "id": "q1_current_role",
    "category": "current_state",
    "type": "select",
    "prompt": "¿Cuál es tu rol actual?",
    "required": true,
    "options": [
      {
        "value": "swe",
        "label": "Ingeniero de Software"
      },
      {
        "value": "senior_swe",
        "label": "Ingeniero de Software Senior/Staff"
      },
      {
        "value": "pm",
        "label": "Product Manager"
      },
      {
        "value": "senior_pm",
        "label": "PM Senior/de Grupo/Director"
      },
      {
        "value": "da",
        "label": "Analista de Datos"
      },
      {
        "value": "ds",
        "label": "Científico de Datos"
      },
      {
        "value": "em",
        "label": "Gerente de Ingeniería"
      },
      {
        "value": "designer",
        "label": "Diseñador de Producto"
      },
      {
        "value": "other",
        "label": "Otro"
      }
    ]
  },
  {
    "id": "q2_years_experience",
    "category": "current_state",
    "type": "select",
    "prompt": "¿Cuántos años de experiencia profesional tienes?",
    "required": true,
    "options": [
      {
        "value": "0-2",
        "label": "0–2 años"
      },
      {
        "value": "3-5",
        "label": "3–5 años"
      },
      {
        "value": "6-10",
        "label": "6–10 años"
      },
      {
        "value": "11-15",
        "label": "11–15 años"
      },
      {
        "value": "16+",
        "label": "16+ años"
      }
    ]
  },
  {
    "id": "q3_ai_exposure",
    "category": "current_state",
    "type": "select",
    "prompt": "¿Cuánto trabajo práctico con IA/LLM has hecho en tu rol actual?",
    "helpText": "No cursos — desplegar o construir con IA en un contexto real.",
    "required": true,
    "options": [
      {
        "value": "none",
        "label": "Nada / muy poco"
      },
      {
        "value": "side",
        "label": "Solo proyectos personales"
      },
      {
        "value": "partial",
        "label": "Algo en el trabajo — herramientas o funciones pequeñas"
      },
      {
        "value": "significant",
        "label": "Significativo — funciones de IA propias en producción"
      },
      {
        "value": "leading",
        "label": "Ya lidero iniciativas de IA"
      }
    ]
  },
  {
    "id": "q4_skill_matrix",
    "category": "current_state",
    "type": "skill_matrix",
    "prompt": "Puntúate en cada dominio (0 = sin exposición, 5 = podrías enseñarlo)",
    "required": true,
    "skillRows": [
      {
        "key": "d1",
        "label": "Prompting e Ingeniería de Contexto",
        "definition": "Prompts de sistema, few-shot, defensas contra inyección de prompts, gestión de la ventana de contexto"
      },
      {
        "key": "d2",
        "label": "Patrones Agénticos",
        "definition": "ReAct, planner-executor, multiagente, HITL — cuándo usar cada uno"
      },
      {
        "key": "d3",
        "label": "Uso e Integración de Herramientas",
        "definition": "Function calling, MCP, esquemas de herramientas, orquestación de APIs"
      },
      {
        "key": "d4",
        "label": "Evaluación y Confiabilidad",
        "definition": "Conjuntos de evaluación, verificaciones de grounding, pruebas adversarias, Ragas/LangSmith"
      },
      {
        "key": "d5",
        "label": "Diseño de Sistemas (específico de IA)",
        "definition": "Arquitecturas RAG, almacenes vectoriales, compromisos de costo/latencia, despliegue"
      },
      {
        "key": "d6",
        "label": "Seguridad y Gobernanza",
        "definition": "Inyección de prompts, patrones de rechazo, controles HITL, registros de auditoría"
      },
      {
        "key": "d7",
        "label": "Pensamiento de Producto (IA)",
        "definition": "Qué hace que una función de IA sea apta para lanzar, manejar el no-determinismo, la UX de la incertidumbre"
      }
    ]
  },
  {
    "id": "q5_target_role",
    "category": "target_state",
    "type": "select",
    "prompt": "¿Qué rol buscas?",
    "required": true,
    "options": [
      {
        "value": "ai_eng",
        "label": "Ingeniero de IA/ML"
      },
      {
        "value": "agentic_eng",
        "label": "Ingeniero de IA Agéntica"
      },
      {
        "value": "ai_pm",
        "label": "Product Manager de IA"
      },
      {
        "value": "ai_platform_pm",
        "label": "PM de Plataforma de IA"
      },
      {
        "value": "applied_ml",
        "label": "Ingeniero de ML Aplicado"
      },
      {
        "value": "ai_research_eng",
        "label": "Ingeniero de Investigación en IA"
      },
      {
        "value": "exploring",
        "label": "Aún explorando — quiero que el roadmap me ayude a decidir"
      }
    ]
  },
  {
    "id": "q6_target_seniority",
    "category": "target_state",
    "type": "select",
    "prompt": "¿Qué nivel de seniority buscas?",
    "required": true,
    "options": [
      {
        "value": "mid",
        "label": "Nivel medio (equivalente a IC3/IC4)"
      },
      {
        "value": "senior",
        "label": "Senior (equivalente a IC5)"
      },
      {
        "value": "staff",
        "label": "Staff (equivalente a IC6)"
      },
      {
        "value": "principal",
        "label": "Principal+ (equivalente a IC7+)"
      },
      {
        "value": "flexible",
        "label": "Flexible — lo que requiera el rol"
      }
    ]
  },
  {
    "id": "q7_timeline",
    "category": "target_state",
    "type": "select",
    "prompt": "Plazo objetivo de transición",
    "required": true,
    "options": [
      {
        "value": "3mo",
        "label": "3 meses (agresivo — ya estás cerca)"
      },
      {
        "value": "6mo",
        "label": "6 meses (estándar)"
      },
      {
        "value": "12mo",
        "label": "12 meses (exhaustivo — desarrollando profundidad)"
      },
      {
        "value": "18mo+",
        "label": "18+ meses (reconstrucción mayor)"
      }
    ]
  },
  {
    "id": "q8_target_companies",
    "category": "target_state",
    "type": "multi_select",
    "prompt": "¿Qué tipos de empresa buscas? (selecciona todas las que apliquen)",
    "required": true,
    "options": [
      {
        "value": "frontier_labs",
        "label": "Laboratorios de IA de frontera (Anthropic, OpenAI, DeepMind)"
      },
      {
        "value": "faang",
        "label": "FAANG con inversión significativa en IA"
      },
      {
        "value": "ai_startup",
        "label": "Startup nativa de IA (seed–Serie B)"
      },
      {
        "value": "ai_scale",
        "label": "Scale-up nativa de IA (Serie C+)"
      },
      {
        "value": "traditional",
        "label": "Empresa tradicional que adopta IA (fintech, salud, empresarial)"
      },
      {
        "value": "consulting",
        "label": "Consultoría / agencia"
      }
    ]
  },
  {
    "id": "q9_current_role_status",
    "category": "constraints",
    "type": "select",
    "prompt": "¿Cuál es tu situación laboral actual?",
    "required": true,
    "options": [
      {
        "value": "employed_stable",
        "label": "Empleado, estable — transicionando deliberadamente"
      },
      {
        "value": "employed_unstable",
        "label": "Empleado pero en riesgo (despidos, desempeño, organización)"
      },
      {
        "value": "between",
        "label": "Actualmente entre roles"
      },
      {
        "value": "consulting",
        "label": "Consultoría / freelance"
      },
      {
        "value": "sabbatical",
        "label": "En pausa intencional / año sabático"
      }
    ]
  },
  {
    "id": "q10_time_availability",
    "category": "constraints",
    "type": "select",
    "prompt": "¿Cuánto tiempo por semana puedes dedicar a la transición?",
    "helpText": "Realista, no aspiracional. Los planes que asumen 20 hrs/semana cuando tienes 5 fracasan.",
    "required": true,
    "options": [
      {
        "value": "5-10",
        "label": "5–10 hrs/semana (solo por las tardes)"
      },
      {
        "value": "10-15",
        "label": "10–15 hrs/semana (lo más común)"
      },
      {
        "value": "15-20",
        "label": "15–20 hrs/semana (lo más común)"
      },
      {
        "value": "20-30",
        "label": "20–30 hrs/semana (compromiso significativo)"
      },
      {
        "value": "40+",
        "label": "40+ hrs/semana (dedicación completa a la transición)"
      }
    ]
  },
  {
    "id": "q11_runway",
    "category": "constraints",
    "type": "select",
    "prompt": "Si renunciaras hoy, ¿cuántos meses de colchón financiero tendrías?",
    "helpText": "Aproximado — lo usamos para calibrar estrategias de mantener-el-empleo vs. puente-de-empleo vs. renunciar-al-empleo.",
    "required": true,
    "options": [
      {
        "value": "<3",
        "label": "< 3 meses"
      },
      {
        "value": "3-6",
        "label": "3–6 meses"
      },
      {
        "value": "6-12",
        "label": "6–12 meses"
      },
      {
        "value": "12-24",
        "label": "12–24 meses"
      },
      {
        "value": "24+",
        "label": "24+ meses / sin restricción de colchón"
      }
    ]
  },
  {
    "id": "q12_geographic",
    "category": "constraints",
    "type": "select",
    "prompt": "Restricciones geográficas / de ubicación de trabajo",
    "required": true,
    "options": [
      {
        "value": "remote_only",
        "label": "Solo remoto (totalmente distribuido)"
      },
      {
        "value": "hybrid_us",
        "label": "Híbrido EE. UU. (SF / NYC / Seattle / Austin OK)"
      },
      {
        "value": "hybrid_eu",
        "label": "Híbrido UE (Londres / Berlín / París)"
      },
      {
        "value": "local_only",
        "label": "Solo local (metrópolis específica)"
      },
      {
        "value": "flexible",
        "label": "Flexible, incluida reubicación"
      },
      {
        "value": "visa_dependent",
        "label": "Dependiente de visa (importa el patrocinio)"
      }
    ]
  },
  {
    "id": "q13_risk_tolerance",
    "category": "preferences",
    "type": "slider",
    "prompt": "Tolerancia al riesgo",
    "helpText": "1 = prefiero interno / bajo riesgo · 5 = renunciar e ir totalmente externo / alto riesgo",
    "required": true,
    "scaleMin": 1,
    "scaleMax": 5,
    "scaleLabels": [
      "Bajo riesgo — se prefiere interno",
      "Alto riesgo — movimiento audaz OK"
    ]
  },
  {
    "id": "q14_portfolio_status",
    "category": "preferences",
    "type": "select",
    "prompt": "¿Cuál es el estado actual de tu portafolio / proyectos?",
    "required": true,
    "options": [
      {
        "value": "none",
        "label": "Aún no tengo trabajo público específico de IA"
      },
      {
        "value": "starting",
        "label": "Algunos tutoriales pequeños / proyectos hello-world"
      },
      {
        "value": "in_progress",
        "label": "1–2 proyectos en curso, aún no lanzados"
      },
      {
        "value": "shipped_1",
        "label": "1 proyecto sólido lanzado"
      },
      {
        "value": "shipped_multi",
        "label": "Múltiples proyectos lanzados con traces + evals"
      }
    ]
  },
  {
    "id": "q15_biggest_worry",
    "category": "preferences",
    "type": "select",
    "prompt": "¿Cuál es tu mayor preocupación sobre esta transición?",
    "required": true,
    "options": [
      {
        "value": "fundamentals",
        "label": "No tengo los fundamentos"
      },
      {
        "value": "portfolio",
        "label": "No tengo evidencia de trabajo lanzado"
      },
      {
        "value": "interviews",
        "label": "Fracasaré en las entrevistas aunque tenga las habilidades"
      },
      {
        "value": "narrative",
        "label": "No podré explicar el giro de manera creíble"
      },
      {
        "value": "finances",
        "label": "Dinero — no puedo permitirme un vacío ni aceptar la oferta equivocada"
      },
      {
        "value": "age_bias",
        "label": "Sesgo de edad / nivel en la contratación de IA"
      },
      {
        "value": "analysis_paralysis",
        "label": "Sigo reiniciando el plan y no lanzo nada"
      }
    ]
  }
],
};

// ---------------------------------------------------------------------------
// Roadmap Archetypes (what the AI generator classifies candidates into)
// ---------------------------------------------------------------------------

export interface RoadmapArchetype {
  key: string;
  name: string;
  description: string;
  primaryChallenge: string;
  targetStrategy: string;
  suitableFor: string; // description of which assessment profiles map here
}

export const ROADMAP_ARCHETYPES: Localized<RoadmapArchetype[]> = {
  en: [
  {
    key: "fast_pivot",
    name: "Fast Pivot",
    description:
      "You're close — strong fundamentals, some AI exposure, external or internal role inside 3 months.",
    primaryChallenge: "Crystallizing your narrative and shipping ONE credible portfolio project fast.",
    targetStrategy:
      "Skip foundations, skip tutorials. 2 weeks on the best-fit archetype project (Portfolio Kit), 2 weeks on resume + LinkedIn repositioning, 2–4 weeks on interview loops.",
    suitableFor: "SWE/PM with AI exposure, 5+ yrs exp, stable employment, 3-month timeline",
  },
  {
    key: "standard_build",
    name: "Standard Build",
    description:
      "You have strong adjacent skills but need to build AI-specific depth. 6-month thoughtful transition.",
    primaryChallenge: "Building technical depth across the 7 domains while maintaining current-role performance.",
    targetStrategy:
      "6 weeks of structured Agentic AI Mastery, 4 weeks of portfolio (1 flagship + 1 secondary), 4 weeks of positioning + interview prep, 4 weeks of active search.",
    suitableFor: "SWE/PM/DS with no significant AI work, 5-15 yrs exp, 10-20 hrs/week available",
  },
  {
    key: "deep_rebuild",
    name: "Deep Rebuild",
    description:
      "You're making a bigger jump — different role family or starting further back. 12-month runway needed.",
    primaryChallenge: "Building engineering fundamentals AND AI-specific depth. Patience is the rare virtue.",
    targetStrategy:
      "3 months on engineering fundamentals (if needed), 3 months on Agentic AI Mastery, 3 months on 2-3 portfolio projects, 3 months on active search.",
    suitableFor: "Non-engineers moving to engineering, DA/DS going to AI Eng, anyone with < 5 hrs/week",
  },
  {
    key: "internal_first",
    name: "Internal First",
    description:
      "Your company has AI initiatives you can join. Internal transition has asymmetric upside — make it your Plan A.",
    primaryChallenge: "Being seen as the 'new you' past your old-role reputation.",
    targetStrategy:
      "4 weeks of internal positioning (conversations, visible small wins in AI-adjacent work), 8 weeks skill building with internal visibility, set a 6-month internal deadline, Plan B external search in parallel at month 4.",
    suitableFor: "Employed at a company doing AI work, strong internal reputation, risk-averse, flexible timeline",
  },
  {
    key: "cash_bridge",
    name: "Cash Bridge",
    description:
      "Between roles or at-risk. Financial pressure is real. Can't afford a 6-month prep.",
    primaryChallenge: "Getting A role quickly that pays and provides AI exposure, then leveling up from there.",
    targetStrategy:
      "2 weeks on tightest-possible portfolio (1 project), immediate active search while continuing to ship, take the first role that offers AI exposure even if it's not the target role, re-plan for the target after 9–12 months.",
    suitableFor: "Between roles with < 6 months runway, layoff risk, financial pressure",
  },
  {
    key: "staff_level_up",
    name: "Staff-Level Up",
    description:
      "You're senior/staff in adjacent domain and targeting staff+ AI role. The transition is also a promotion.",
    primaryChallenge: "Demonstrating senior/staff-level AI judgment, not just AI skills. Leveling proof.",
    targetStrategy:
      "Multi-agent project (LeadResearcher pattern) as flagship, system-design practice at staff level, thought-leadership content (1 strong technical post), network activation with staff-level AI ICs, deliberately target ambiguity-heavy roles where your senior judgment premium is visible.",
    suitableFor: "Senior/Staff IC, 10+ yrs, targeting Staff/Principal in AI, willing to spend 6+ months",
  },
  ],
  es: [
  {
    "key": "fast_pivot",
    "name": "Cambio Rápido",
    "description": "Estás cerca: fundamentos sólidos, algo de exposición a IA, un rol externo o interno en menos de 3 meses.",
    "primaryChallenge": "Cristalizar tu narrativa y lanzar UN proyecto de portafolio creíble rápidamente.",
    "targetStrategy": "Sáltate los fundamentos, sáltate los tutoriales. 2 semanas en el proyecto de arquetipo que mejor encaje (Portfolio Kit), 2 semanas en el reposicionamiento del currículum + LinkedIn, 2–4 semanas en las rondas de entrevistas.",
    "suitableFor": "SWE/PM con exposición a IA, más de 5 años de experiencia, empleo estable, plazo de 3 meses"
  },
  {
    "key": "standard_build",
    "name": "Construcción Estándar",
    "description": "Tienes habilidades adyacentes sólidas, pero necesitas desarrollar profundidad específica en IA. Una transición meditada de 6 meses.",
    "primaryChallenge": "Desarrollar profundidad técnica en los 7 dominios mientras mantienes el rendimiento en tu rol actual.",
    "targetStrategy": "6 semanas de Agentic AI Mastery estructurado, 4 semanas de portafolio (1 insignia + 1 secundario), 4 semanas de posicionamiento + preparación de entrevistas, 4 semanas de búsqueda activa.",
    "suitableFor": "SWE/PM/DS sin trabajo significativo en IA, 5-15 años de experiencia, 10-20 hrs/semana disponibles"
  },
  {
    "key": "deep_rebuild",
    "name": "Reconstrucción Profunda",
    "description": "Estás dando un salto mayor: una familia de roles diferente o partiendo desde más atrás. Necesitas una pista de 12 meses.",
    "primaryChallenge": "Desarrollar fundamentos de ingeniería Y profundidad específica en IA. La paciencia es la virtud escasa.",
    "targetStrategy": "3 meses en fundamentos de ingeniería (si es necesario), 3 meses en Agentic AI Mastery, 3 meses en 2-3 proyectos de portafolio, 3 meses de búsqueda activa.",
    "suitableFor": "No ingenieros que pasan a ingeniería, DA/DS que van a AI Eng, cualquiera con menos de 5 hrs/semana"
  },
  {
    "key": "internal_first",
    "name": "Interno Primero",
    "description": "Tu empresa tiene iniciativas de IA a las que puedes unirte. La transición interna tiene un potencial asimétrico: conviértela en tu Plan A.",
    "primaryChallenge": "Ser visto como el 'nuevo tú' más allá de la reputación de tu rol anterior.",
    "targetStrategy": "4 semanas de posicionamiento interno (conversaciones, pequeñas victorias visibles en trabajo adyacente a IA), 8 semanas de desarrollo de habilidades con visibilidad interna, fijar un plazo interno de 6 meses, búsqueda externa de Plan B en paralelo en el mes 4.",
    "suitableFor": "Empleado en una empresa que hace trabajo de IA, reputación interna sólida, aversión al riesgo, plazo flexible"
  },
  {
    "key": "cash_bridge",
    "name": "Puente Financiero",
    "description": "Entre roles o en riesgo. La presión financiera es real. No puedes permitirte una preparación de 6 meses.",
    "primaryChallenge": "Conseguir UN rol rápidamente que pague y ofrezca exposición a IA, y luego escalar desde ahí.",
    "targetStrategy": "2 semanas en el portafolio más ajustado posible (1 proyecto), búsqueda activa inmediata mientras sigues lanzando, aceptar el primer rol que ofrezca exposición a IA aunque no sea el rol objetivo, replanificar hacia el objetivo después de 9–12 meses.",
    "suitableFor": "Entre roles con menos de 6 meses de pista, riesgo de despido, presión financiera"
  },
  {
    "key": "staff_level_up",
    "name": "Ascenso a Staff",
    "description": "Eres senior/staff en un dominio adyacente y apuntas a un rol de IA de nivel staff+. La transición también es un ascenso.",
    "primaryChallenge": "Demostrar criterio de IA a nivel senior/staff, no solo habilidades de IA. Prueba de nivel.",
    "targetStrategy": "Proyecto multiagente (patrón LeadResearcher) como insignia, práctica de diseño de sistemas a nivel staff, contenido de liderazgo de opinión (1 publicación técnica sólida), activación de la red con ICs de IA a nivel staff, apuntar deliberadamente a roles con alta ambigüedad donde tu prima de criterio senior sea visible.",
    "suitableFor": "IC Senior/Staff, más de 10 años, apuntando a Staff/Principal en IA, dispuesto a dedicar más de 6 meses"
  }
],
};

// ---------------------------------------------------------------------------
// Tier pricing for the roadmap generator — display copy
// ---------------------------------------------------------------------------

export interface RoadmapTierDisplayItem {
  tier: TierGate;
  label: string;
  generations: string;
  sublabel: string;
  color: string;
}

export const ROADMAP_TIER_DISPLAY: Localized<RoadmapTierDisplayItem[]> = {
  en: [
  {
    tier: "free" as TierGate,
    label: "Free",
    generations: "1 roadmap · lifetime",
    sublabel: "Your first personalized plan is on us. Execute on it with the overview content.",
    color: "#38a169",
  },
  {
    tier: "pro" as TierGate,
    label: `Pro · ${fmtUSD(PRICING.pro.monthly)}/mo`,
    generations: "3 roadmaps / month",
    sublabel: "Re-run as your situation evolves — new offer, shifted timeline, different target.",
    color: "#2b6cb0",
  },
  {
    tier: "all_access" as TierGate,
    label: `All Access · ${fmtUSD(PRICING.allAccess.monthly)}/mo`,
    generations: "Unlimited",
    sublabel: "Full playbook + unlimited regenerations + all 6 unique-content frameworks.",
    color: "#7c3aed",
  },
  ],
  es: [
  {
    "tier": "free",
    "label": "Free",
    "generations": "1 roadmap · de por vida",
    "sublabel": "Tu primer plan personalizado corre por nuestra cuenta. Ejecútalo con el contenido general.",
    "color": "#38a169"
  },
  {
    "tier": "pro",
    "label": "Pro · $39/mes",
    "generations": "3 roadmaps / mes",
    "sublabel": "Vuelve a ejecutarlo a medida que evoluciona tu situación: nueva oferta, cambio de plazos, objetivo distinto.",
    "color": "#2b6cb0"
  },
  {
    "tier": "all_access",
    "label": "All Access · $129/mes",
    "generations": "Ilimitado",
    "sublabel": "Playbook completo + regeneraciones ilimitadas + los 6 frameworks de contenido único.",
    "color": "#7c3aed"
  }
],
};

// ---------------------------------------------------------------------------
// Framework deep-dive content
//
// Each section is one of several types rendered differently by FrameworkPage:
//   - "prose": narrative paragraphs
//   - "checklist": list of bullet items
//   - "table": rows of labeled cells
//   - "script": verbatim dialogue / email with tone notes
//   - "decision-tree": if/then branches
//   - "template": fill-in-the-blanks copy
//   - "callout": insight or warning block
// ---------------------------------------------------------------------------

export type SectionKind =
  | "prose"
  | "checklist"
  | "table"
  | "script"
  | "decision-tree"
  | "template"
  | "callout";

export interface FrameworkSection {
  kind: SectionKind;
  title: string;
  subtitle?: string;
  body?: string; // for prose, template, callout
  items?: string[]; // for checklist
  rows?: { label: string; body: string }[]; // for table, decision-tree
  calloutStyle?: "insight" | "warning" | "counter"; // visual variant for callouts
}

export interface FrameworkDeepContent {
  overview: string; // 1-paragraph scene-setter shown above sections
  readingTime: string;
  sections: FrameworkSection[];
}

// ---------------------------------------------------------------------------
// 1. Readiness Assessment framework (the free one — mostly routes to the
//    actual assessment flow, but carries overview content on the deep-dive).
// ---------------------------------------------------------------------------

const READINESS_ASSESSMENT_CONTENT: FrameworkDeepContent = {
  overview:
    "The 15-question readiness assessment is the front door to every other framework in this product. It classifies your situation, surfaces the specific risks you'll hit, and generates a personalized roadmap that routes each task to the right CareerForge product at the right moment. Most career transition advice is generic because it has to be — it's written for a hypothetical reader. This is the opposite.",
  readingTime: "10 min assessment · instant roadmap",
  sections: [
    {
      kind: "prose",
      title: "Why sequence matters more than content",
      body: "The standard failure mode is doing the right things in the wrong order. Examples: shipping a portfolio project before you understand evaluation rigor (and the project quietly looks amateur). Repositioning your resume before your skill story is true (and interviewers smell it in 30 seconds). Doing interview prep before you have credible evidence (and wasting expensive recruiter conversations). The assessment's job is not to tell you WHAT to work on — you know that — but to tell you the sequence that compounds versus the sequence that cancels itself out.",
    },
    {
      kind: "callout",
      title: "What the assessment actually does",
      calloutStyle: "insight",
      body: "Inputs — current role, skill self-rating across the 7 agentic domains, timeline, financial runway, portfolio status, risk tolerance. Outputs — readiness score (calibrated across 5 dimensions), archetype classification (1 of 6), a personalized phased plan where every task routes into the specific CareerForge product that solves that task's gap, and the single primary risk for your profile.",
    },
    {
      kind: "prose",
      title: "What the archetypes mean",
      body: "Your assessment classifies you into one of 6 archetypes based on your situation, not your ambition. Fast Pivot: you're close, 3-month search makes sense. Standard Build: strong adjacent skills, 6-month deliberate transition. Deep Rebuild: bigger jump, 12-month runway. Internal First: your current company has AI work, try there first. Cash Bridge: financial pressure is real, take the first AI-exposure role and re-plan. Staff Level-Up: senior IC targeting staff+ AI roles, the pivot is also a promotion. The roadmap structure adapts significantly across these — a Cash Bridge plan looks nothing like a Staff Level-Up plan.",
    },
    {
      kind: "checklist",
      title: "When to re-run the assessment",
      items: [
        "You get a surprise offer (internal or external) that shifts your timeline or decision",
        "A major skill-gap assumption changes (e.g., you shipped a big AI project at work)",
        "Your financial runway changes materially (layoff, windfall, partner change)",
        "You change target company types (e.g., initially frontier labs, now scale-ups)",
        "Your target seniority band shifts (you decide to go for Staff, not Senior)",
        "~3 months have passed — quarterly calibration catches drift",
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// 2. Transition Narrative Frameworks
// ---------------------------------------------------------------------------

const TRANSITION_NARRATIVES_CONTENT: FrameworkDeepContent = {
  overview:
    "Weak transition narratives are the silent-killer of pivots. Hiring managers pattern-match in 30 seconds: are you running TOWARD something (curiosity, growth, compounding fit) or running AWAY (burnout, bad manager, layoff fear)? The second set isn't disqualifying, but it requires a different narrative structure, and most candidates don't know the difference. This framework gives you the 3-thread skeleton plus channel-specific variants.",
  readingTime: "45 min",
  sections: [
    {
      kind: "prose",
      title: "The 3-thread framework",
      body: "Every credible transition narrative braids three threads: (1) What you're KEEPING — the continuity, the accumulated asset that makes your next role possible; (2) What you're ADDING — the growth, the specific capability you're seeking; (3) What you're LEAVING BEHIND — the honest tradeoff. Weak narratives show only 1 or 2 threads. 'I want to move from PM to AI PM because I'm excited about AI' has Adding but no Keeping (so you sound like a dilettante) and no Leaving Behind (so you sound dishonest). Strong narratives include all three.",
    },
    {
      kind: "template",
      title: "The 3-thread narrative template",
      body: `"[KEEPING] Over the last [N] years as a [current role], I've built [specific capability] — most recently shipping [concrete example with metric].
[ADDING] What I've realized through [trigger: a project, a conversation, a study] is that the problems I'm most engaged by are [specific AI-adjacent problem space]. I want to spend the next chapter building the [specific technical/product skill] to own those problems.
[LEAVING BEHIND] I'm honest that this means stepping back from [specific thing: management scope, a specific tech stack, a seniority signal] to get to the new thing. That trade-off is right for me right now because [specific reason rooted in your career arc, not generic 'passion']."`,
    },
    {
      kind: "table",
      title: "7 narrative archetypes",
      rows: [
        { label: "Expansion", body: "Your current role is working; you want to add AI to your scope. Emphasizes Keeping, Adding is light." },
        { label: "Return", body: "You were technical, went non-technical, returning to technical. Nostalgia is a trap — lead with what you learned in the non-technical years." },
        { label: "Repositioning", body: "Same function, AI domain. PM → AI PM. DS → AI Eng. Emphasize domain knowledge that transfers." },
        { label: "Escape", body: "Your current org or situation is bad. Most dangerous archetype — you must lead with Adding, never Leaving Behind, or you sound desperate." },
        { label: "Curiosity-driven", body: "You're exploring. Works for senior candidates who can afford it; dangerous for pivoters who can't." },
        { label: "Forced", body: "Layoff, company failure. Acknowledge briefly and pivot immediately to why AI is what's next." },
        { label: "Maturity", body: "You've grown into wanting harder problems. Best archetype if genuine; weakest if performed." },
      ],
    },
    {
      kind: "script",
      title: "Interview answer — 60 seconds",
      subtitle: 'For "Why are you making this transition?" — Expansion archetype',
      body: `"I've spent seven years as a senior backend engineer at [company], most recently leading the payments reliability team — we took p99 from 2.3s to 340ms over 18 months.

Last year I picked up an internal AI project almost by accident: we were getting too many customer-support tickets to triage manually, and I prototyped a RAG-based classifier. It shipped, it worked, and it was the most engaging six weeks of my year. What struck me wasn't the AI part — it was that the reliability instincts I'd built in payments suddenly mattered in a new, harder way. Evaluation, tracing, cost budgets — it's the same discipline applied to a less-understood substrate.

I want to go deeper on that substrate. Which means stepping back from the management track I was on at [company] and rebuilding as an IC in agentic systems. I'm honest that's a trade-off — I'm trading scope for depth — but the depth is where my unfair advantage is, and this is the right decade to build it."`,
    },
    {
      kind: "checklist",
      title: "The 'Why now?' follow-up — the unmasker",
      items: [
        "Interviewers ask 'Why now?' specifically because it separates strong from weak narratives",
        "Wrong answer: 'AI is a hot space' (everyone knows, says nothing about you)",
        "Wrong answer: 'I've been thinking about this for a while' (vague = performative)",
        "Right answer anchors to a specific trigger: a project, a conversation, a moment of clarity tied to a concrete event",
        "Bonus: include the trade-off you made to create the window (quit side project X, turned down Y, pushed back on Z) — shows this is real, not aspirational",
      ],
    },
    {
      kind: "callout",
      title: "Channel-specific variants",
      calloutStyle: "insight",
      body: "Interview (60-90s spoken): full 3-thread structure with one concrete trigger. LinkedIn headline (8 words): just the Adding thread plus domain. Resume cover note (3 sentences): Keeping + Adding, skip Leaving Behind. Current manager conversation (5 min): lead with Keeping (reassure), introduce Adding, acknowledge Leaving Behind as a tradeoff for growth. Different channel, different depth, same underlying structure.",
    },
    {
      kind: "callout",
      title: "What gets you caught",
      calloutStyle: "warning",
      body: "References. If your narrative says 'I want more technical depth' but your references describe you as someone who avoided technical work, the mismatch surfaces in 24 hours. Your narrative must be true in the sense your references would confirm it. Draft the narrative, then stress-test it against what your last three managers would say about you — unedited.",
    },
  ],
};

// ---------------------------------------------------------------------------
// 3. Financial Bridge Planning
// ---------------------------------------------------------------------------

const FINANCIAL_BRIDGE_CONTENT: FrameworkDeepContent = {
  overview:
    "Most career transition content ignores money, which is why most career transitions fail around month 4. Financial pressure is what causes candidates to accept mediocre offers after preparing for great ones. This framework makes the runway math explicit, names the three bridge archetypes, and gives you decision triggers so you know when to hold and when to fold.",
  readingTime: "60 min + worksheet",
  sections: [
    {
      kind: "callout",
      title: "The leverage-buffer insight",
      calloutStyle: "insight",
      body: "Your minimum viable runway is NOT 'months of expenses saved.' It is 'months of expenses saved MINUS expected-offer-delay MINUS negotiation-leverage-buffer.' The leverage buffer is the number of months of runway you need remaining AT the moment you receive an offer, in order to negotiate rather than capitulate. A typical leverage buffer is 2-3 months. If you have 6 months saved and it takes 4 months to land an offer, you have 2 months of leverage buffer — which is minimum viable. If it takes 5 months, you have 1 month — you will accept whatever comes.",
    },
    {
      kind: "table",
      title: "The 3 bridge archetypes",
      rows: [
        { label: "Job-Hold", body: "Stay employed, learn on evenings/weekends. Pros: infinite runway, low financial risk. Cons: slower learning (10-15 hrs/week max), narrative risk if employer finds out, psychological exhaustion by month 6. Best for: stable employment with flexible hours, 9-12 month timeline." },
        { label: "Job-Bridge", body: "Exit into contract/consulting at lower intensity while building. Pros: maintains income, frees mental cycles, often gives AI-adjacent exposure via client work. Cons: consulting work can become a trap, eats 20-30 hrs/week, 1099 taxes hurt. Best for: Senior+ candidates with existing network, 6-9 month timeline." },
        { label: "Job-Quit", body: "Pure runway, full-time on transition. Pros: fastest possible skill acquisition, clean narrative ('I took a quarter off to retool'), 40+ hrs/week possible. Cons: the leverage buffer drops fast, psychologically brutal, market can look strange mid-quarter. Best for: 12+ months of runway, 3-4 month timeline, strong baseline skills." },
      ],
    },
    {
      kind: "template",
      title: "Runway math worksheet",
      body: `Step 1 — Fixed monthly expenses
  Rent/mortgage: $___
  Utilities + internet + phone: $___
  Healthcare (COBRA or ACA): $___
  Insurance (car/life/disability): $___
  Debt minimums (student loans, etc.): $___
  Childcare / dependent care: $___
  Groceries (realistic, not aspirational): $___
  TOTAL FIXED: $___ / month  [A]

Step 2 — Variable expenses (honest average, last 6 months)
  Transport + travel: $___
  Subscriptions + software: $___
  Eating out + social: $___
  Household + personal: $___
  Miscellaneous: $___
  TOTAL VARIABLE: $___ / month  [B]

Step 3 — Monthly burn rate = [A] + [B] = $___  [C]

Step 4 — Current savings accessible within 30 days
  Cash/HYSA: $___
  Taxable brokerage (after 15% sell tax): $___
  Do NOT include: 401k, IRA, home equity unless you'd actually tap it
  TOTAL ACCESSIBLE: $___  [D]

Step 5 — Raw runway = [D] / [C] = ___ months  [E]

Step 6 — Expected offer-delay (be honest)
  With existing network + portfolio evidence: 2-4 months
  Without: 4-8 months
  Name it: ___ months  [F]

Step 7 — Leverage buffer (months of runway you want REMAINING at offer time)
  Conservative: 3 months
  Standard: 2 months
  Aggressive: 1 month (warning: you will accept the first offer)
  Pick yours: ___ months  [G]

Step 8 — Minimum viable runway at day-of-quit = [F] + [G] = ___ months  [H]

DECISION: If [E] >= [H], Job-Quit is viable.
If [E] is between ([H] - 3) and [H], consider Job-Bridge.
If [E] < [H] - 3, Job-Hold is the safe play.`,
    },
    {
      kind: "checklist",
      title: "Tax-efficient bridge strategies",
      items: [
        "HSA max-contribute while still employed (healthcare becomes a bigger line in transition)",
        "401k loan vs withdrawal: loan is usually wrong unless you're 100% sure of return timing; withdrawal is a 10% penalty below 59.5 that's rarely worth it",
        "Backdoor Roth: if you're quitting in January, last year's pre-tax income is still in play",
        "LLC S-corp for consulting income: below ~$80k annual revenue it's usually not worth the admin overhead; above, yes",
        "State arbitrage: quit in a no-income-tax state if relocation is on the table",
        "Health insurance: COBRA is usually the worst option; ACA subsidies apply once your expected annual income drops below the subsidy cliff",
      ],
    },
    {
      kind: "decision-tree",
      title: "When to accept the first offer vs. wait",
      rows: [
        { label: "Accept if", body: "Offer is within 10% of target comp AND company/role/team is top-3 of your wishlist AND leverage buffer [G] is ≥ 2 months." },
        { label: "Negotiate but lean accept", body: "Offer is 10-20% below target AND company is top-3 AND leverage buffer is ≥ 1 month. Counter on scope + 6-month review, not just comp." },
        { label: "Walk away", body: "Offer is >20% below target AND leverage buffer is ≥ 2 months AND you have active pipeline. Rare, but the right call when real." },
        { label: "Accept even if mediocre", body: "Leverage buffer is 0 or negative, offer is within 15% of market. Do not let the perfect be the enemy of the staying-employed. Re-plan transition #2 from the new role." },
      ],
    },
    {
      kind: "callout",
      title: "The offer-timing asymmetry",
      calloutStyle: "insight",
      body: "Your first month of job-searching is worth 3x your fourth. Early in your search you have fresh narrative, fresh network warmth, unspent interview energy, and your runway feels infinite. By month 4, all of those are degraded. Front-load everything: the 5 highest-priority outreach, the two companies you'd most want, the prep work that makes the first offer the best offer. Candidates who pace themselves 'to keep pipeline flowing' frequently discover the pipeline was Month 1, not Month 4.",
    },
  ],
};

// ---------------------------------------------------------------------------
// 4. Negotiation for Pivoters
// ---------------------------------------------------------------------------

const PIVOTER_NEGOTIATION_CONTENT: FrameworkDeepContent = {
  overview:
    "Lateral-move negotiation advice gets pivoters underpaid. Your leverage sources are different when you're changing roles: you are partly a 'risk hire' in the hiring manager's head, and both of you know it. The five moves in this framework work BECAUSE of that asymmetry, not against it. The three destroyers at the end are the ones that feel sophisticated and silently cost pivoters ten to forty thousand dollars.",
  readingTime: "50 min",
  sections: [
    {
      kind: "callout",
      title: "Your actual leverage as a pivoter",
      calloutStyle: "insight",
      body: "You are NOT leveraging 'competing offers' or 'current comp' the way lateral-movers do. Your leverage is: (1) the hiring team has decided you're the right risk to take — the sunk cost of their decision is your leverage; (2) the ramp risk they're absorbing is real and you can help de-risk it; (3) you have time flexibility most lateral hires don't. Negotiate from the strength they already agreed to see in you, not from market-comp arguments that don't apply cleanly to your profile.",
    },
    {
      kind: "table",
      title: "The 5 pivoter-specific moves",
      rows: [
        { label: "1. Scope-review trigger", body: "Ask for a defined 6-month review tied to concrete deliverables, with comp adjustment built in. The HM is comfortable because it's milestone-gated; you're comfortable because it forcing-functions the conversation. Single highest-leverage move for pivoters." },
        { label: "2. Signing bonus as comp-gap bridge", body: "If base is 10% below target, asking for a larger signing bonus (spread over 2 years with clawback) often works where asking for base increase doesn't. HMs have budget flexibility in bonus pools they don't have in base bands." },
        { label: "3. Growth-path contract", body: "Not a formal contract — a written email that documents what promotion looks like (concrete criteria, expected timeline). Reduces political risk of 'the pivot hire that stayed junior forever.' Makes the risk visible on both sides." },
        { label: "4. Reference-network protection", body: "If you're paying a reputational cost to leave (your old boss is angry, your old network thinks you're making a mistake), name that explicitly. 'My current team is upset. I'd like to cleanly transition — I need [signing bonus / extended start date / title parity] to make the change make sense to the people watching.' Often works on senior HMs." },
        { label: "5. Timeline flexibility", body: "You can start in 2 weeks, 2 months, or 4 months. HMs value timeline certainty. Offering flexibility (especially later starts that let them budget in the right fiscal quarter) can be worth meaningful comp." },
      ],
    },
    {
      kind: "table",
      title: "The 3 destroyers (do NOT do these)",
      rows: [
        { label: "1. Lowballing yourself", body: "Pivoters consistently aim 15-25% below what the market would actually pay them. They justify it as 'reasonable given my ramp.' It's not reasonable. Ask for 90th-percentile of the band for your target level — you will get 60-70th percentile, which is more than you would have asked for." },
        { label: "2. Comparing to peers in your OLD role", body: "'My current peers make X, so I should accept Y.' Your old peer comp is noise for your new role. You're in a different market now. Reference the levels.fyi/comp bands for the target role and company, period." },
        { label: "3. Accepting 'we need to see you perform'", body: "If an HM says 'let's revisit comp in 6 months when we see how you're doing,' this is either the scope-review trigger (good) OR a permanent underpricing (bad). Force the distinction by asking for the specific criteria for the review in writing. If they can't name criteria, it's the underpricing version — negotiate now." },
      ],
    },
    {
      kind: "script",
      title: "Opening the negotiation",
      subtitle: "After they've made a verbal offer, before you counter",
      body: `"Thank you — really excited about this and the team. I want to take 24-48 hours to think through the details properly so I can come back with a thoughtful response rather than a fast one.

While I'm thinking through it, can you help me understand two things?

First — how does this offer compare to other pivoters the team has brought in? I want to calibrate, because I know pivoter comp is different and I'd rather get clarity up front than haggle.

Second — what does the 6-month performance conversation look like, and is there a concrete path to re-opening comp at that point if I hit defined deliverables?

No rush on answers — happy to pick this up Monday. But I'd rather ask now than discover later."`,
    },
    {
      kind: "script",
      title: "The counter",
      subtitle: "After they've responded to your opening",
      body: `"I've thought about this carefully. Here's where I'd like to land:

Base: $[15-20k above their base] — I'd anchor this to the 75th percentile of the band for [target level], which reflects the full stack I'm bringing even though the domain is new.

Signing bonus: $[30-50k] with the standard 24-month clawback — I'm paying a transition cost on reputation and network (my current team is not thrilled) and this helps bridge that.

Growth path: I'd like a written commitment to a 6-month scope review tied to [specific deliverable 1], [specific deliverable 2], [specific deliverable 3], with comp adjustment to [target level +1] if those are met.

Timeline: I can start [date 3 weeks out] or [date 8 weeks out] — whichever is better for your team's planning.

None of this is a hill I'll die on individually — but I'd like to find a package that gets us close to these numbers. What's feasible?"`,
    },
    {
      kind: "callout",
      title: "Silence is a tactic",
      calloutStyle: "insight",
      body: "After you deliver your counter, stop talking. Most pivoters negotiate against themselves in the 30 seconds of silence after the counter — 'I mean, I'm flexible on base if the signing is real' — and cut 8% off their own ask. Let them respond first. If the silence is excruciating, that is the work; it's not a sign something is wrong. Practice this. The single most leverage-losing habit in pivoter negotiations is filling silence.",
    },
    {
      kind: "checklist",
      title: "When to take less money (real cases)",
      items: [
        "The role has unique access to mentors who will compound your career value for 3+ years",
        "The company is at an inflection point where equity upside is asymmetric",
        "The scope lets you build a deliberate 2-year portfolio piece you could not build elsewhere",
        "You're solving a specific insecurity (leveling proof, domain establishment) that will pay back in negotiation #2",
        "NOT legitimate reasons: 'they really liked me,' 'I need to get into AI,' 'I've been searching too long,' 'imposter syndrome'",
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// 5. First 90 Days on an AI Team
// ---------------------------------------------------------------------------

const FIRST_90_DAYS_CONTENT: FrameworkDeepContent = {
  overview:
    "The hiring decision is not final until day 90. AI teams in particular are aggressive about managing out pivoters who don't ramp. Your first 90 days determine whether you're seen as the 'great hire we took a chance on' or 'the mistake we have to work around.' This playbook sequences the week-by-week moves that preempt the 'did we hire the right person?' conversation before it starts.",
  readingTime: "40 min",
  sections: [
    {
      kind: "callout",
      title: "The single highest-leverage first-90 move",
      calloutStyle: "insight",
      body: "Ship a small, visible, COMPLETE win in week 3-5. Not your biggest project — your fastest complete loop. Pick something that takes 2 weeks, has clear success criteria, and is genuinely useful to someone on the team. The completion re-frames every conversation about your ramp from 'are they fast enough?' to 'what should we give them next?' This single move is worth more than the quality of your first-month technical ramp.",
    },
    {
      kind: "table",
      title: "Week-by-week playbook",
      rows: [
        { label: "Week 1-2: Observation", body: "Do not attempt to 'make your mark.' Meet everyone on the team 1:1 (ask 4 questions: what are you working on, what's hardest, what's broken that no one's fixing, who should I learn from). Read the last 6 months of relevant docs + PRs. Ship nothing bigger than a small bug fix. Map the political landscape. Listen for the problem that no one on the team is solving — that's often your opening." },
        { label: "Week 3-5: First visible ship", body: "Pick the fastest-possible complete project. Optimize for visibility over technical impressiveness. Write a Loom-style walkthrough. Share it in the relevant Slack channels, not just with your manager. Ask for code review from 2 people you respect — invites them into your work early, which builds trust faster than doing it solo and announcing the result." },
        { label: "Week 6-8: Domain owner conversation", body: "By now you've observed enough to know which sub-domain is most under-owned. Propose to your manager: 'I'd like to own [specific scope] — here's why I think I'm the right person and what it would unlock.' Getting a claimed scope early prevents the 'they float around and never own anything' pattern that derails pivoters in month 4-6." },
        { label: "Week 9-12: The 90-day conversation", body: "Your manager will likely schedule a formal 90-day review. Don't wait for it — propose it yourself in week 10. Walk in with: (1) what you shipped, (2) what you learned about the team/domain, (3) what you propose to own in the next 90 days. Having the proposal land before the review makes the review approving you, not evaluating you." },
      ],
    },
    {
      kind: "script",
      title: "Week 1 introduction Slack post",
      subtitle: "Post this in your team channel on day 2, not day 1",
      body: `"Hey [team] — jumping in here. Context on me: I come from a [SWE/PM/DS] background, most recently [one sentence about your last role and one specific thing you shipped that sounds relevant].

I'm explicitly pivoting into agentic AI engineering — this team is my first AI role. That means I'm going to ask basic questions in my first few weeks and I'd rather do that loudly than silently be confused. If I ask you something that feels obvious, that's exactly what I want to be doing.

I'd love 25-minute 1:1s with as many of you as possible over the next 2 weeks. I'll reach out individually. In the meantime — what's one doc / PR / meeting recording I should watch to understand what this team actually does day-to-day?"`,
    },
    {
      kind: "checklist",
      title: "Anti-patterns (how pivoters self-sabotage)",
      items: [
        "Criticizing existing code in your first 30 days. Even if it's bad. Especially if it's bad.",
        "Volunteering for too much in your first week — you can't deliver and you've over-committed publicly",
        "Pretending to know something to avoid looking junior. Everyone sees it. Everyone.",
        "Rewriting someone's code without asking them first. Universal political landmine, applies 2x to pivoters.",
        "Making your 90-day review the first time your manager hears you have a scope proposal. Propose earlier.",
        "Skipping 1:1s with senior ICs who aren't your manager. They decide whether you're seen as credible much more than your manager does.",
        "Hiding effort. If you spent 3 days on something that should have taken 1, say so in the PR description. Honesty about pace early buys trust that pays back in month 3.",
      ],
    },
    {
      kind: "callout",
      title: "The failure pattern to preempt",
      calloutStyle: "warning",
      body: "Pivoters often get fired at month 4-6, not day 90. Why? Because the team gave them a 'benefit of the doubt' extension at day 90 based on 'they're ramping,' and then the 'ramp' never produced a visible win. Weeks 3-5 (first visible ship) and weeks 9-12 (domain-owner conversation) exist specifically to prevent this. A pivoter who has claimed scope and shipped one clean deliverable by day 90 is essentially safe. One who hasn't is on a 90-day timer.",
    },
    {
      kind: "callout",
      title: "What to say in your first team all-hands",
      calloutStyle: "insight",
      body: "You WILL be asked to introduce yourself. Keep it under 45 seconds. Hit three beats: (1) what you come FROM in 1 sentence, (2) why you came to THIS team specifically (research the team's public writing or products before day 1 so you can reference something concrete), (3) what you want to learn in your first 90 days. Do NOT: tell your whole career arc, explain your transition narrative in detail, promise specific deliverables. Save all that for the people who actually need it.",
    },
  ],
};

// ---------------------------------------------------------------------------
// 6. Internal vs. External Transition Decision
// ---------------------------------------------------------------------------

const INTERNAL_VS_EXTERNAL_CONTENT: FrameworkDeepContent = {
  overview:
    "Internal transitions look safer but often fail because the hiring manager can't see the 'new you' past the old one. External transitions look riskier but frequently result in better titles, comp, and scope. The 12-factor framework makes the trade-off explicit. The dominant factor is usually reference credibility: strong reputation in the target domain, internal wins; strong in an unrelated domain, external wins.",
  readingTime: "35 min",
  sections: [
    {
      kind: "callout",
      title: "The hidden dominant factor",
      calloutStyle: "insight",
      body: "Reference credibility is usually the factor that decides. If your current colleagues would describe you as 'strong in [the target domain],' internal transition is typically right — their belief is a huge lever. If they'd describe you as 'strong in [something else],' external is typically right — a fresh read will outperform the echo of your old reputation. The other 11 factors matter at the margin; this one moves the whole decision.",
    },
    {
      kind: "table",
      title: "The 12 factors",
      rows: [
        { label: "Time to role", body: "Internal: usually 2-4 months. External: 4-9 months. Internal wins on speed alone if you're in a rush." },
        { label: "Comp delta", body: "Internal typically +0-10% (bound by internal equity). External typically +15-30%. External wins on comp unless internal equity is surprisingly flexible." },
        { label: "Role quality", body: "Internal depends on what AI work your company is doing. External lets you choose the best available role. If your company's AI work is mediocre, external dominates." },
        { label: "Reference credibility", body: "See the callout above. Often the dominant factor." },
        { label: "Political risk", body: "Internal carries risk of being trapped between teams, or your old team blocking your transfer. External is a clean break." },
        { label: "Learning velocity", body: "External usually wins because you're forced to learn faster to ramp. Internal can be slower because your existing reputation protects you from the intensity." },
        { label: "Mentor access", body: "Where are the senior AI ICs you'd learn from? This factor alone can dominate for staff+ candidates." },
        { label: "Product quality", body: "External lets you pick companies with strong AI products. Internal you're stuck with whatever your company has." },
        { label: "Team strength", body: "Judge by who's on the team you'd join, not the company brand. Small strong teams at second-tier companies often beat average teams at first-tier ones." },
        { label: "Org AI maturity", body: "Is your current org 'adding AI' or 'AI-native'? 'Adding AI' companies often lose their best AI hires within 18 months." },
        { label: "Exit optionality", body: "External role carries an implicit 'I can leave' optionality. Internal you've used one. Under-valued until you need it." },
        { label: "Psychological cost", body: "Explaining the transition repeatedly to colleagues is exhausting. External is one conversation with yourself, then done." },
      ],
    },
    {
      kind: "decision-tree",
      title: "3 worked examples",
      rows: [
        { label: "SWE at Stripe — stays internal", body: "Stripe has a strong ML infra team shipping real AI work. Reference credibility inside Stripe is high for the target domain (backend → ML infra is a short jump). Internal team is top-tier. Factors that dominate: reference credibility + product quality + team strength. Internal wins." },
        { label: "PM at Meta — goes external", body: "Meta AI PM roles exist but are politically fraught and the org is in constant re-org. Her reference credibility inside Meta is for consumer surface area, not AI. External offers at AI-native scale-ups pay 25% more, give cleaner scope, and let her build a new reputation from scratch. Factors: role quality + political risk + comp delta + exit optionality. External wins." },
        { label: "DA at Stitch Fix — stays internal with deadline", body: "Stitch Fix has a genuine ML org. Her DA reputation is actually in demand forecasting, which is ML-adjacent. Internal transition possible in 3 months. External transition possible in 9 months. She tries internal first with a 90-day deadline — if she hasn't progressed to an AI-team rotation by then, she goes external. Factors: time to role + reference credibility (moderate). Hybrid strategy." },
      ],
    },
    {
      kind: "callout",
      title: "The internal-first rule",
      calloutStyle: "insight",
      body: "Default to trying internal FIRST — with a deadline. Three months of internal effort is cheap (you're still employed, your external resume isn't going out, your network isn't activated). At the 3-month mark, assess honestly: have you progressed, or have you been strung along? If strung along, pivot hard to external. Running internal + external in parallel from day one is exhausting and often signals desperation to both sides. Sequential beats parallel.",
    },
    {
      kind: "checklist",
      title: "3 red flags — do NOT try internal first",
      items: [
        "Your current org has a documented pattern of promising internal transitions that don't happen (ask your network — this is usually well-known)",
        "Your current manager's permission is required AND your manager has signaled they don't support the transition",
        "Your target internal team is in a different cost center / geography and transferring would require cross-org politics that typically take 6+ months",
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Content lookup
// ---------------------------------------------------------------------------

export const FRAMEWORK_DEEP_CONTENT: Localized<Record<string, FrameworkDeepContent>> = {
  en: {
    "readiness-assessment": READINESS_ASSESSMENT_CONTENT,
    "transition-narratives": TRANSITION_NARRATIVES_CONTENT,
    "financial-bridge": FINANCIAL_BRIDGE_CONTENT,
    "pivoter-negotiation": PIVOTER_NEGOTIATION_CONTENT,
    "first-90-days": FIRST_90_DAYS_CONTENT,
    "internal-vs-external": INTERNAL_VS_EXTERNAL_CONTENT,
  },
  es: {
  "readiness-assessment": {
    "overview": "La evaluación de preparación de 15 preguntas es la puerta de entrada a todos los demás frameworks de este producto. Clasifica tu situación, revela los riesgos específicos que enfrentarás y genera una hoja de ruta personalizada que dirige cada tarea al producto adecuado de CareerForge en el momento adecuado. La mayoría de los consejos sobre transiciones profesionales son genéricos porque tienen que serlo: están escritos para un lector hipotético. Esto es lo contrario.",
    "readingTime": "10 min de evaluación · hoja de ruta instantánea",
    "sections": [
      {
        "kind": "prose",
        "title": "Por qué la secuencia importa más que el contenido",
        "body": "El modo de fallo estándar es hacer las cosas correctas en el orden equivocado. Ejemplos: publicar un proyecto de portafolio antes de comprender el rigor de la evaluación (y el proyecto queda discretamente amateur). Reposicionar tu currículum antes de que tu historia de habilidades sea verdadera (y los entrevistadores lo huelen en 30 segundos). Prepararte para entrevistas antes de tener evidencia creíble (y desperdiciar valiosas conversaciones con reclutadores). El trabajo de la evaluación no es decirte QUÉ trabajar —eso ya lo sabes— sino decirte la secuencia que se acumula frente a la secuencia que se anula a sí misma."
      },
      {
        "kind": "callout",
        "title": "Qué hace realmente la evaluación",
        "calloutStyle": "insight",
        "body": "Entradas: rol actual, autoevaluación de habilidades en los 7 dominios agénticos, cronograma, margen financiero, estado del portafolio, tolerancia al riesgo. Salidas: puntuación de preparación (calibrada en 5 dimensiones), clasificación de arquetipo (1 de 6), un plan personalizado por fases donde cada tarea se dirige al producto específico de CareerForge que resuelve la carencia de esa tarea, y el único riesgo principal para tu perfil."
      },
      {
        "kind": "prose",
        "title": "Qué significan los arquetipos",
        "body": "Tu evaluación te clasifica en uno de 6 arquetipos según tu situación, no tu ambición. Cambio Rápido: estás cerca, una búsqueda de 3 meses tiene sentido. Construcción Estándar: habilidades adyacentes sólidas, transición deliberada de 6 meses. Reconstrucción Profunda: un salto mayor, margen de 12 meses. Interno Primero: tu empresa actual tiene trabajo de IA, prueba allí primero. Puente Financiero: la presión financiera es real, toma el primer rol con exposición a IA y vuelve a planificar. Ascenso a Staff: IC senior que apunta a roles de IA de nivel staff+, el giro también es una promoción. La estructura de la hoja de ruta se adapta significativamente entre ellos: un plan Puente Financiero no se parece en nada a un plan Ascenso a Staff."
      },
      {
        "kind": "checklist",
        "title": "Cuándo volver a hacer la evaluación",
        "items": [
          "Recibes una oferta sorpresa (interna o externa) que cambia tu cronograma o decisión",
          "Cambia una suposición importante sobre una carencia de habilidades (p. ej., publicaste un gran proyecto de IA en el trabajo)",
          "Tu margen financiero cambia materialmente (despido, golpe de suerte, cambio de pareja)",
          "Cambias los tipos de empresa objetivo (p. ej., inicialmente laboratorios de frontera, ahora scale-ups)",
          "Tu banda de seniority objetivo cambia (decides ir por Staff, no Senior)",
          "Han pasado ~3 meses: la calibración trimestral detecta la deriva"
        ]
      }
    ]
  },
  "transition-narratives": {
    "overview": "Las narrativas de transición débiles son el asesino silencioso de los giros de carrera. Los gerentes de contratación hacen pattern-matching en 30 segundos: ¿corres HACIA algo (curiosidad, crecimiento, encaje que se acumula) o corres LEJOS de algo (agotamiento, mal jefe, miedo a un despido)? El segundo conjunto no es descalificante, pero requiere una estructura narrativa diferente, y la mayoría de los candidatos no conocen la diferencia. Este framework te da el esqueleto de 3 hilos más variantes específicas por canal.",
    "readingTime": "45 min",
    "sections": [
      {
        "kind": "prose",
        "title": "El framework de 3 hilos",
        "body": "Toda narrativa de transición creíble entrelaza tres hilos: (1) Lo que estás CONSERVANDO: la continuidad, el activo acumulado que hace posible tu próximo rol; (2) Lo que estás AÑADIENDO: el crecimiento, la capacidad específica que buscas; (3) Lo que estás DEJANDO ATRÁS: el sacrificio honesto. Las narrativas débiles muestran solo 1 o 2 hilos. 'Quiero pasar de PM a AI PM porque me entusiasma la IA' tiene el Añadir pero no el Conservar (así que suenas a diletante) y no el Dejar Atrás (así que suenas deshonesto). Las narrativas fuertes incluyen los tres."
      },
      {
        "kind": "template",
        "title": "La plantilla narrativa de 3 hilos",
        "body": "\"[CONSERVANDO] En los últimos [N] años como [rol actual], he construido [capacidad específica]: más recientemente publicando [ejemplo concreto con métrica].\n[AÑADIENDO] Lo que he comprendido a través de [detonante: un proyecto, una conversación, un estudio] es que los problemas que más me involucran son [espacio de problemas específico adyacente a la IA]. Quiero dedicar el próximo capítulo a construir la [habilidad técnica/de producto específica] para adueñarme de esos problemas.\n[DEJANDO ATRÁS] Soy honesto en que esto significa dar un paso atrás respecto de [cosa específica: alcance de gestión, un stack tecnológico específico, una señal de seniority] para llegar a lo nuevo. Ese sacrificio es el correcto para mí ahora mismo porque [razón específica arraigada en tu trayectoria profesional, no una 'pasión' genérica].\""
      },
      {
        "kind": "table",
        "title": "7 arquetipos narrativos",
        "rows": [
          {
            "label": "Expansión",
            "body": "Tu rol actual funciona; quieres añadir IA a tu alcance. Enfatiza el Conservar, el Añadir es ligero."
          },
          {
            "label": "Retorno",
            "body": "Eras técnico, pasaste a lo no técnico, vuelves a lo técnico. La nostalgia es una trampa: lidera con lo que aprendiste en los años no técnicos."
          },
          {
            "label": "Reposicionamiento",
            "body": "Misma función, dominio de IA. PM → AI PM. DS → AI Eng. Enfatiza el conocimiento del dominio que se transfiere."
          },
          {
            "label": "Escape",
            "body": "Tu organización o situación actual es mala. El arquetipo más peligroso: debes liderar con el Añadir, nunca con el Dejar Atrás, o sonarás desesperado."
          },
          {
            "label": "Impulsado por la curiosidad",
            "body": "Estás explorando. Funciona para candidatos senior que pueden permitírselo; peligroso para quienes hacen un giro y no pueden."
          },
          {
            "label": "Forzado",
            "body": "Despido, quiebra de la empresa. Reconócelo brevemente y gira de inmediato hacia por qué la IA es lo que sigue."
          },
          {
            "label": "Madurez",
            "body": "Has crecido hasta querer problemas más difíciles. El mejor arquetipo si es genuino; el más débil si es fingido."
          }
        ]
      },
      {
        "kind": "script",
        "title": "Respuesta de entrevista — 60 segundos",
        "subtitle": "Para \"¿Por qué haces esta transición?\" — arquetipo Expansión",
        "body": "\"He pasado siete años como ingeniero backend senior en [empresa], más recientemente liderando el equipo de fiabilidad de pagos: llevamos el p99 de 2,3s a 340ms en 18 meses.\n\nEl año pasado tomé un proyecto interno de IA casi por accidente: recibíamos demasiados tickets de soporte al cliente para triagearlos manualmente, y prototipé un clasificador basado en RAG. Se publicó, funcionó y fueron las seis semanas más estimulantes de mi año. Lo que me impactó no fue la parte de IA: fue que los instintos de fiabilidad que había construido en pagos de repente importaban de una forma nueva y más difícil. Evaluación, trazabilidad, presupuestos de costos: es la misma disciplina aplicada a un sustrato menos comprendido.\n\nQuiero profundizar en ese sustrato. Lo que significa dar un paso atrás respecto de la vía de gestión en la que estaba en [empresa] y reconstruirme como IC en sistemas agénticos. Soy honesto en que es un sacrificio —cambio alcance por profundidad— pero la profundidad es donde está mi ventaja injusta, y esta es la década correcta para construirla.\""
      },
      {
        "kind": "checklist",
        "title": "La pregunta de seguimiento '¿Por qué ahora?' — la que desenmascara",
        "items": [
          "Los entrevistadores preguntan '¿Por qué ahora?' específicamente porque separa las narrativas fuertes de las débiles",
          "Respuesta incorrecta: 'La IA es un espacio candente' (todos lo saben, no dice nada de ti)",
          "Respuesta incorrecta: 'Llevo un tiempo pensando en esto' (vago = fingido)",
          "La respuesta correcta se ancla a un detonante específico: un proyecto, una conversación, un momento de claridad ligado a un evento concreto",
          "Extra: incluye el sacrificio que hiciste para crear la ventana (abandonaste el proyecto paralelo X, rechazaste Y, cuestionaste Z): demuestra que esto es real, no aspiracional"
        ]
      },
      {
        "kind": "callout",
        "title": "Variantes específicas por canal",
        "calloutStyle": "insight",
        "body": "Entrevista (60-90s hablados): estructura completa de 3 hilos con un detonante concreto. Titular de LinkedIn (8 palabras): solo el hilo del Añadir más el dominio. Nota de presentación del currículum (3 oraciones): Conservar + Añadir, omite el Dejar Atrás. Conversación con el gerente actual (5 min): lidera con el Conservar (tranquiliza), introduce el Añadir, reconoce el Dejar Atrás como un sacrificio por crecimiento. Diferente canal, diferente profundidad, misma estructura subyacente."
      },
      {
        "kind": "callout",
        "title": "Qué te delata",
        "calloutStyle": "warning",
        "body": "Las referencias. Si tu narrativa dice 'quiero más profundidad técnica' pero tus referencias te describen como alguien que evitaba el trabajo técnico, la discrepancia sale a la luz en 24 horas. Tu narrativa debe ser verdadera en el sentido de que tus referencias la confirmarían. Redacta la narrativa y luego sométela a prueba contra lo que dirían de ti tus últimos tres gerentes, sin edición."
      }
    ]
  },
  "financial-bridge": {
    "overview": "La mayoría del contenido sobre transiciones profesionales ignora el dinero, y por eso la mayoría de las transiciones fallan alrededor del mes 4. La presión financiera es lo que lleva a los candidatos a aceptar ofertas mediocres tras haberse preparado para grandes ofertas. Este framework hace explícita la matemática del margen, nombra los tres arquetipos de puente y te da detonantes de decisión para que sepas cuándo aguantar y cuándo rendirte.",
    "readingTime": "60 min + hoja de trabajo",
    "sections": [
      {
        "kind": "callout",
        "title": "El insight del margen de apalancamiento",
        "calloutStyle": "insight",
        "body": "Tu margen mínimo viable NO es 'meses de gastos ahorrados'. Es 'meses de gastos ahorrados MENOS el retraso esperado de la oferta MENOS el margen de apalancamiento de negociación'. El margen de apalancamiento es la cantidad de meses de margen que necesitas que te queden EN el momento en que recibas una oferta, para poder negociar en lugar de capitular. Un margen de apalancamiento típico es de 2 a 3 meses. Si tienes 6 meses ahorrados y tardas 4 meses en conseguir una oferta, tienes 2 meses de margen de apalancamiento, que es el mínimo viable. Si tardas 5 meses, tienes 1 mes: aceptarás lo que venga."
      },
      {
        "kind": "table",
        "title": "Los 3 arquetipos de puente",
        "rows": [
          {
            "label": "Job-Hold",
            "body": "Permanece empleado, aprende por las tardes/fines de semana. Pros: margen infinito, bajo riesgo financiero. Contras: aprendizaje más lento (10-15 hrs/semana máximo), riesgo narrativo si el empleador se entera, agotamiento psicológico para el mes 6. Ideal para: empleo estable con horario flexible, cronograma de 9-12 meses."
          },
          {
            "label": "Job-Bridge",
            "body": "Sal hacia trabajo por contrato/consultoría a menor intensidad mientras construyes. Pros: mantiene ingresos, libera ciclos mentales, a menudo da exposición adyacente a la IA mediante el trabajo con clientes. Contras: el trabajo de consultoría puede convertirse en una trampa, consume 20-30 hrs/semana, los impuestos 1099 duelen. Ideal para: candidatos Senior+ con red existente, cronograma de 6-9 meses."
          },
          {
            "label": "Job-Quit",
            "body": "Margen puro, tiempo completo en la transición. Pros: la adquisición de habilidades más rápida posible, narrativa limpia ('me tomé un trimestre para reciclarme'), 40+ hrs/semana posibles. Contras: el margen de apalancamiento baja rápido, psicológicamente brutal, el mercado puede verse extraño a mitad del trimestre. Ideal para: 12+ meses de margen, cronograma de 3-4 meses, habilidades base sólidas."
          }
        ]
      },
      {
        "kind": "template",
        "title": "Hoja de trabajo de matemática del margen",
        "body": "Paso 1 — Gastos fijos mensuales\n  Alquiler/hipoteca: $___\n  Servicios + internet + teléfono: $___\n  Salud (COBRA o ACA): $___\n  Seguros (auto/vida/discapacidad): $___\n  Mínimos de deuda (préstamos estudiantiles, etc.): $___\n  Cuidado de hijos / dependientes: $___\n  Comestibles (realista, no aspiracional): $___\n  TOTAL FIJO: $___ / mes  [A]\n\nPaso 2 — Gastos variables (promedio honesto, últimos 6 meses)\n  Transporte + viajes: $___\n  Suscripciones + software: $___\n  Comer fuera + social: $___\n  Hogar + personal: $___\n  Varios: $___\n  TOTAL VARIABLE: $___ / mes  [B]\n\nPaso 3 — Tasa de consumo mensual = [A] + [B] = $___  [C]\n\nPaso 4 — Ahorros actuales accesibles en 30 días\n  Efectivo/HYSA: $___\n  Cuenta de corretaje gravable (después del 15% de impuesto por venta): $___\n  NO incluir: 401k, IRA, capital de la vivienda a menos que realmente los usarías\n  TOTAL ACCESIBLE: $___  [D]\n\nPaso 5 — Margen bruto = [D] / [C] = ___ meses  [E]\n\nPaso 6 — Retraso esperado de la oferta (sé honesto)\n  Con red existente + evidencia de portafolio: 2-4 meses\n  Sin ella: 4-8 meses\n  Nómbralo: ___ meses  [F]\n\nPaso 7 — Margen de apalancamiento (meses de margen que quieres que te QUEDEN al momento de la oferta)\n  Conservador: 3 meses\n  Estándar: 2 meses\n  Agresivo: 1 mes (advertencia: aceptarás la primera oferta)\n  Elige el tuyo: ___ meses  [G]\n\nPaso 8 — Margen mínimo viable el día de la renuncia = [F] + [G] = ___ meses  [H]\n\nDECISIÓN: Si [E] >= [H], Job-Quit es viable.\nSi [E] está entre ([H] - 3) y [H], considera Job-Bridge.\nSi [E] < [H] - 3, Job-Hold es la jugada segura."
      },
      {
        "kind": "checklist",
        "title": "Estrategias de puente fiscalmente eficientes",
        "items": [
          "Aportación máxima a HSA mientras aún estás empleado (la salud se vuelve una partida mayor en la transición)",
          "Préstamo 401k vs retiro: el préstamo suele ser un error a menos que estés 100% seguro del momento de tu regreso; el retiro tiene una penalización del 10% antes de los 59.5 que rara vez vale la pena",
          "Backdoor Roth: si vas a renunciar en enero, el ingreso antes de impuestos del año pasado aún está en juego",
          "LLC S-corp para ingresos de consultoría: por debajo de ~$80k de ingresos anuales normalmente no vale la sobrecarga administrativa; por encima, sí",
          "Arbitraje estatal: renuncia en un estado sin impuesto a la renta si la reubicación es una opción",
          "Seguro de salud: COBRA suele ser la peor opción; los subsidios de ACA aplican una vez que tu ingreso anual esperado cae por debajo del umbral del subsidio"
        ]
      },
      {
        "kind": "decision-tree",
        "title": "Cuándo aceptar la primera oferta vs. esperar",
        "rows": [
          {
            "label": "Acepta si",
            "body": "La oferta está dentro del 10% de la compensación objetivo Y la empresa/rol/equipo es top-3 de tu lista de deseos Y el margen de apalancamiento [G] es ≥ 2 meses."
          },
          {
            "label": "Negocia pero inclínate a aceptar",
            "body": "La oferta está 10-20% por debajo del objetivo Y la empresa es top-3 Y el margen de apalancamiento es ≥ 1 mes. Contra-oferta sobre alcance + revisión a los 6 meses, no solo sobre compensación."
          },
          {
            "label": "Retírate",
            "body": "La oferta está >20% por debajo del objetivo Y el margen de apalancamiento es ≥ 2 meses Y tienes un pipeline activo. Raro, pero la decisión correcta cuando es real."
          },
          {
            "label": "Acepta aunque sea mediocre",
            "body": "El margen de apalancamiento es 0 o negativo, la oferta está dentro del 15% del mercado. No dejes que lo perfecto sea enemigo de seguir empleado. Vuelve a planificar la transición #2 desde el nuevo rol."
          }
        ]
      },
      {
        "kind": "callout",
        "title": "La asimetría del momento de la oferta",
        "calloutStyle": "insight",
        "body": "Tu primer mes de búsqueda de empleo vale 3 veces más que tu cuarto. Al inicio de tu búsqueda tienes una narrativa fresca, calidez fresca en tu red, energía de entrevista sin gastar y tu margen se siente infinito. Para el mes 4, todo eso está degradado. Adelanta todo: los 5 contactos de mayor prioridad, las dos empresas que más querrías, el trabajo de preparación que hace que la primera oferta sea la mejor oferta. Los candidatos que se dosifican 'para mantener el pipeline fluyendo' con frecuencia descubren que el pipeline era el Mes 1, no el Mes 4."
      }
    ]
  },
  "pivoter-negotiation": {
    "overview": "Los consejos de negociación para movimientos laterales dejan mal pagados a quienes hacen un giro. Tus fuentes de apalancamiento son diferentes cuando cambias de rol: en parte eres una 'contratación de riesgo' en la cabeza del gerente de contratación, y ambos lo saben. Los cinco movimientos de este framework funcionan PRECISAMENTE por esa asimetría, no contra ella. Los tres destructores al final son los que se sienten sofisticados y silenciosamente les cuestan a quienes hacen un giro entre diez y cuarenta mil dólares.",
    "readingTime": "50 min",
    "sections": [
      {
        "kind": "callout",
        "title": "Tu apalancamiento real como pivoter",
        "calloutStyle": "insight",
        "body": "NO estás apalancando 'ofertas competidoras' ni 'compensación actual' como lo hacen quienes se mueven lateralmente. Tu apalancamiento es: (1) el equipo de contratación ha decidido que eres el riesgo correcto que vale la pena tomar; el costo hundido de su decisión es tu apalancamiento; (2) el riesgo de rampa que están absorbiendo es real y tú puedes ayudar a reducirlo; (3) tienes flexibilidad de tiempo que la mayoría de las contrataciones laterales no tienen. Negocia desde la fortaleza que ya acordaron ver en ti, no desde argumentos de compensación de mercado que no aplican limpiamente a tu perfil."
      },
      {
        "kind": "table",
        "title": "Los 5 movimientos específicos para pivoters",
        "rows": [
          {
            "label": "1. Detonante de revisión de alcance",
            "body": "Pide una revisión definida a los 6 meses ligada a entregables concretos, con un ajuste de compensación incorporado. El HM se siente cómodo porque está condicionado a hitos; tú te sientes cómodo porque fuerza la conversación. El movimiento de mayor apalancamiento para pivoters."
          },
          {
            "label": "2. Bono de contratación como puente de brecha de compensación",
            "body": "Si el salario base está 10% por debajo del objetivo, pedir un bono de contratación mayor (repartido en 2 años con cláusula de devolución) a menudo funciona donde pedir un aumento de base no lo hace. Los HM tienen flexibilidad de presupuesto en los fondos de bonos que no tienen en las bandas de base."
          },
          {
            "label": "3. Contrato de trayectoria de crecimiento",
            "body": "No un contrato formal: un correo escrito que documente cómo se ve la promoción (criterios concretos, cronograma esperado). Reduce el riesgo político de 'la contratación pivoter que se quedó junior para siempre'. Hace visible el riesgo para ambas partes."
          },
          {
            "label": "4. Protección de la red de referencias",
            "body": "Si estás pagando un costo reputacional por irte (tu antiguo jefe está enojado, tu antigua red cree que estás cometiendo un error), nómbralo explícitamente. 'Mi equipo actual está molesto. Me gustaría hacer una transición limpia: necesito [bono de contratación / fecha de inicio extendida / paridad de título] para que el cambio tenga sentido ante la gente que está observando.' A menudo funciona con HM senior."
          },
          {
            "label": "5. Flexibilidad de cronograma",
            "body": "Puedes empezar en 2 semanas, 2 meses o 4 meses. Los HM valoran la certeza del cronograma. Ofrecer flexibilidad (especialmente inicios más tardíos que les permitan presupuestar en el trimestre fiscal correcto) puede valer una compensación significativa."
          }
        ]
      },
      {
        "kind": "table",
        "title": "Los 3 destructores (NO hagas esto)",
        "rows": [
          {
            "label": "1. Subvalorarte a ti mismo",
            "body": "Los pivoters apuntan consistentemente 15-25% por debajo de lo que el mercado realmente les pagaría. Lo justifican como 'razonable dada mi rampa'. No es razonable. Pide el percentil 90 de la banda para tu nivel objetivo: obtendrás el percentil 60-70, que es más de lo que habrías pedido."
          },
          {
            "label": "2. Compararte con pares de tu ANTIGUO rol",
            "body": "'Mis pares actuales ganan X, así que debería aceptar Y.' La compensación de tus antiguos pares es ruido para tu nuevo rol. Ahora estás en un mercado diferente. Referencia las bandas de compensación de levels.fyi para el rol y la empresa objetivo, y punto."
          },
          {
            "label": "3. Aceptar 'necesitamos verte rendir'",
            "body": "Si un HM dice 'revisemos la compensación en 6 meses cuando veamos cómo te va', esto es o bien el detonante de revisión de alcance (bueno) O una subvaloración permanente (malo). Fuerza la distinción pidiendo por escrito los criterios específicos de la revisión. Si no pueden nombrar criterios, es la versión de subvaloración: negocia ahora."
          }
        ]
      },
      {
        "kind": "script",
        "title": "Abrir la negociación",
        "subtitle": "Después de que hayan hecho una oferta verbal, antes de contra-ofertar",
        "body": "\"Gracias, realmente entusiasmado con esto y con el equipo. Quiero tomarme 24-48 horas para pensar bien los detalles y poder volver con una respuesta reflexiva en lugar de una rápida.\n\nMientras lo pienso, ¿me pueden ayudar a entender dos cosas?\n\nPrimero: ¿cómo se compara esta oferta con la de otros pivoters que el equipo ha incorporado? Quiero calibrar, porque sé que la compensación de los pivoters es diferente y prefiero tener claridad de antemano que regatear.\n\nSegundo: ¿cómo se ve la conversación de desempeño a los 6 meses, y hay una trayectoria concreta para reabrir la compensación en ese punto si cumplo entregables definidos?\n\nSin prisa por las respuestas, feliz de retomar esto el lunes. Pero prefiero preguntar ahora que descubrirlo después.\""
      },
      {
        "kind": "script",
        "title": "La contra-oferta",
        "subtitle": "Después de que hayan respondido a tu apertura",
        "body": "\"Lo he pensado con cuidado. Aquí es donde me gustaría aterrizar:\n\nBase: $[15-20k por encima de su base]. Anclaría esto al percentil 75 de la banda para [nivel objetivo], que refleja el stack completo que aporto aunque el dominio sea nuevo.\n\nBono de contratación: $[30-50k] con la cláusula de devolución estándar de 24 meses. Estoy pagando un costo de transición en reputación y red (mi equipo actual no está encantado) y esto ayuda a cubrir eso.\n\nTrayectoria de crecimiento: me gustaría un compromiso por escrito de una revisión de alcance a los 6 meses ligada a [entregable específico 1], [entregable específico 2], [entregable específico 3], con ajuste de compensación a [nivel objetivo +1] si se cumplen.\n\nCronograma: puedo empezar el [fecha a 3 semanas] o el [fecha a 8 semanas], lo que sea mejor para la planificación de tu equipo.\n\nNada de esto es una colina en la que moriré individualmente, pero me gustaría encontrar un paquete que nos acerque a estos números. ¿Qué es factible?\""
      },
      {
        "kind": "callout",
        "title": "El silencio es una táctica",
        "calloutStyle": "insight",
        "body": "Después de entregar tu contra-oferta, deja de hablar. La mayoría de los pivoters negocian contra sí mismos en los 30 segundos de silencio tras la contra-oferta —'bueno, soy flexible con la base si el bono es real'— y se quitan un 8% de su propia petición. Deja que respondan primero. Si el silencio es insoportable, ese es el trabajo; no es señal de que algo esté mal. Practica esto. El único hábito que más apalancamiento pierde en las negociaciones de los pivoters es llenar el silencio."
      },
      {
        "kind": "checklist",
        "title": "Cuándo aceptar menos dinero (casos reales)",
        "items": [
          "El rol tiene acceso único a mentores que harán crecer el valor de tu carrera durante 3+ años",
          "La empresa está en un punto de inflexión donde el potencial de las acciones es asimétrico",
          "El alcance te permite construir una pieza de portafolio deliberada de 2 años que no podrías construir en otro lugar",
          "Estás resolviendo una inseguridad específica (prueba de nivel, establecimiento en un dominio) que se recompensará en la negociación #2",
          "NO son razones legítimas: 'realmente les gusté', 'necesito entrar en la IA', 'llevo demasiado tiempo buscando', 'síndrome del impostor'"
        ]
      }
    ]
  },
  "first-90-days": {
    "overview": "La decisión de contratación no es definitiva hasta el día 90. Los equipos de IA en particular son agresivos a la hora de dejar ir a los pivoters que no toman rampa. Tus primeros 90 días determinan si te ven como 'la gran contratación por la que apostamos' o 'el error con el que tenemos que lidiar'. Este playbook secuencia los movimientos semana a semana que anticipan la conversación '¿contratamos a la persona correcta?' antes de que empiece.",
    "readingTime": "40 min",
    "sections": [
      {
        "kind": "callout",
        "title": "El movimiento de mayor apalancamiento en los primeros 90 días",
        "calloutStyle": "insight",
        "body": "Publica una victoria pequeña, visible y COMPLETA en la semana 3-5. No tu proyecto más grande: tu ciclo completo más rápido. Elige algo que tome 2 semanas, tenga criterios de éxito claros y sea genuinamente útil para alguien del equipo. La finalización reencuadra toda conversación sobre tu rampa de '¿son lo suficientemente rápidos?' a '¿qué deberíamos darles a continuación?'. Este único movimiento vale más que la calidad de tu rampa técnica del primer mes."
      },
      {
        "kind": "table",
        "title": "Playbook semana a semana",
        "rows": [
          {
            "label": "Semana 1-2: Observación",
            "body": "No intentes 'dejar tu huella'. Reúnete 1:1 con todo el equipo (haz 4 preguntas: en qué estás trabajando, qué es lo más difícil, qué está roto y nadie está arreglando, de quién debería aprender). Lee los últimos 6 meses de docs + PRs relevantes. No publiques nada más grande que un pequeño arreglo de bug. Mapea el panorama político. Escucha cuál es el problema que nadie del equipo está resolviendo: a menudo esa es tu apertura."
          },
          {
            "label": "Semana 3-5: Primera publicación visible",
            "body": "Elige el proyecto completo más rápido posible. Optimiza para la visibilidad por encima de lo técnicamente impresionante. Escribe un recorrido estilo Loom. Compártelo en los canales de Slack relevantes, no solo con tu gerente. Pide revisión de código a 2 personas que respetes: los invita temprano a tu trabajo, lo que genera confianza más rápido que hacerlo en solitario y anunciar el resultado."
          },
          {
            "label": "Semana 6-8: Conversación de propiedad de dominio",
            "body": "Para ahora ya has observado lo suficiente para saber cuál subdominio está más falto de dueño. Propón a tu gerente: 'Me gustaría adueñarme de [alcance específico]: aquí está por qué creo que soy la persona correcta y qué desbloquearía'. Conseguir un alcance reclamado temprano previene el patrón de 'flotan por ahí y nunca se adueñan de nada' que descarrila a los pivoters en el mes 4-6."
          },
          {
            "label": "Semana 9-12: La conversación de los 90 días",
            "body": "Es probable que tu gerente programe una revisión formal de 90 días. No esperes a ella: propónla tú mismo en la semana 10. Entra con: (1) lo que publicaste, (2) lo que aprendiste sobre el equipo/dominio, (3) lo que propones adueñarte en los próximos 90 días. Que la propuesta aterrice antes de la revisión hace que la revisión te apruebe, no que te evalúe."
          }
        ]
      },
      {
        "kind": "script",
        "title": "Publicación de presentación en Slack de la semana 1",
        "subtitle": "Publica esto en el canal de tu equipo el día 2, no el día 1",
        "body": "\"Hola [equipo], me sumo por aquí. Contexto sobre mí: vengo de un background de [SWE/PM/DS], más recientemente [una oración sobre tu último rol y una cosa específica que publicaste que suene relevante].\n\nEstoy haciendo un giro explícito hacia la ingeniería de IA agéntica: este equipo es mi primer rol de IA. Eso significa que voy a hacer preguntas básicas en mis primeras semanas y prefiero hacerlo en voz alta que estar confundido en silencio. Si les pregunto algo que parece obvio, eso es exactamente lo que quiero estar haciendo.\n\nMe encantaría tener 1:1 de 25 minutos con la mayor cantidad posible de ustedes en las próximas 2 semanas. Me pondré en contacto individualmente. Mientras tanto: ¿cuál es un doc / PR / grabación de reunión que debería ver para entender qué hace realmente este equipo en el día a día?\""
      },
      {
        "kind": "checklist",
        "title": "Antipatrones (cómo se sabotean los pivoters)",
        "items": [
          "Criticar el código existente en tus primeros 30 días. Aunque sea malo. Especialmente si es malo.",
          "Ofrecerte para demasiado en tu primera semana: no puedes entregar y te has sobrecomprometido públicamente",
          "Fingir que sabes algo para evitar parecer junior. Todos lo ven. Todos.",
          "Reescribir el código de alguien sin preguntarle primero. Mina política universal, aplica el doble a los pivoters.",
          "Hacer que tu revisión de 90 días sea la primera vez que tu gerente escucha que tienes una propuesta de alcance. Propón antes.",
          "Saltarte los 1:1 con IC senior que no son tu gerente. Ellos deciden si te ven como creíble mucho más que tu gerente.",
          "Ocultar el esfuerzo. Si pasaste 3 días en algo que debería haber tomado 1, dilo en la descripción del PR. La honestidad sobre el ritmo desde temprano compra confianza que se recompensa en el mes 3."
        ]
      },
      {
        "kind": "callout",
        "title": "El patrón de fallo que hay que anticipar",
        "calloutStyle": "warning",
        "body": "A los pivoters a menudo los despiden en el mes 4-6, no el día 90. ¿Por qué? Porque el equipo les dio una extensión de 'beneficio de la duda' en el día 90 basada en 'están tomando rampa', y luego la 'rampa' nunca produjo una victoria visible. Las semanas 3-5 (primera publicación visible) y las semanas 9-12 (conversación de propiedad de dominio) existen específicamente para prevenir esto. Un pivoter que ha reclamado alcance y publicado un entregable limpio para el día 90 está esencialmente a salvo. Uno que no lo ha hecho está en un cronómetro de 90 días."
      },
      {
        "kind": "callout",
        "title": "Qué decir en tu primera reunión general del equipo",
        "calloutStyle": "insight",
        "body": "TE pedirán que te presentes. Mantenlo en menos de 45 segundos. Toca tres puntos: (1) de qué VIENES en 1 oración, (2) por qué llegaste a ESTE equipo específicamente (investiga la escritura pública o los productos del equipo antes del día 1 para que puedas referenciar algo concreto), (3) qué quieres aprender en tus primeros 90 días. NO: cuentes toda tu trayectoria profesional, expliques tu narrativa de transición en detalle, prometas entregables específicos. Reserva todo eso para las personas que realmente lo necesitan."
      }
    ]
  },
  "internal-vs-external": {
    "overview": "Las transiciones internas parecen más seguras pero a menudo fallan porque el gerente de contratación no puede ver al 'nuevo tú' más allá del antiguo. Las transiciones externas parecen más arriesgadas pero con frecuencia resultan en mejores títulos, compensación y alcance. El framework de 12 factores hace explícito el sacrificio. El factor dominante suele ser la credibilidad de las referencias: reputación fuerte en el dominio objetivo, ganan las internas; fuerte en un dominio no relacionado, ganan las externas.",
    "readingTime": "35 min",
    "sections": [
      {
        "kind": "callout",
        "title": "El factor dominante oculto",
        "calloutStyle": "insight",
        "body": "La credibilidad de las referencias suele ser el factor que decide. Si tus colegas actuales te describirían como 'fuerte en [el dominio objetivo]', la transición interna suele ser la correcta: su creencia es una palanca enorme. Si te describirían como 'fuerte en [otra cosa]', la externa suele ser la correcta: una lectura fresca superará al eco de tu antigua reputación. Los otros 11 factores importan en el margen; este mueve toda la decisión."
      },
      {
        "kind": "table",
        "title": "Los 12 factores",
        "rows": [
          {
            "label": "Tiempo hasta el rol",
            "body": "Interna: usualmente 2-4 meses. Externa: 4-9 meses. La interna gana solo por velocidad si tienes prisa."
          },
          {
            "label": "Delta de compensación",
            "body": "La interna típicamente +0-10% (limitada por la equidad interna). La externa típicamente +15-30%. La externa gana en compensación a menos que la equidad interna sea sorprendentemente flexible."
          },
          {
            "label": "Calidad del rol",
            "body": "La interna depende del trabajo de IA que esté haciendo tu empresa. La externa te permite elegir el mejor rol disponible. Si el trabajo de IA de tu empresa es mediocre, la externa domina."
          },
          {
            "label": "Credibilidad de las referencias",
            "body": "Ve el destacado de arriba. A menudo el factor dominante."
          },
          {
            "label": "Riesgo político",
            "body": "La interna conlleva el riesgo de quedar atrapado entre equipos, o de que tu antiguo equipo bloquee tu traslado. La externa es un corte limpio."
          },
          {
            "label": "Velocidad de aprendizaje",
            "body": "La externa suele ganar porque te ves obligado a aprender más rápido para tomar rampa. La interna puede ser más lenta porque tu reputación existente te protege de la intensidad."
          },
          {
            "label": "Acceso a mentores",
            "body": "¿Dónde están los IC senior de IA de quienes aprenderías? Este factor por sí solo puede dominar para candidatos staff+."
          },
          {
            "label": "Calidad del producto",
            "body": "La externa te permite elegir empresas con productos de IA fuertes. En la interna te quedas con lo que tu empresa tenga."
          },
          {
            "label": "Fortaleza del equipo",
            "body": "Juzga por quiénes están en el equipo al que te unirías, no por la marca de la empresa. Equipos pequeños y fuertes en empresas de segundo nivel a menudo superan a equipos promedio en empresas de primer nivel."
          },
          {
            "label": "Madurez de IA de la organización",
            "body": "¿Tu organización actual está 'añadiendo IA' o es 'IA-nativa'? Las empresas que 'añaden IA' a menudo pierden a sus mejores contrataciones de IA en 18 meses."
          },
          {
            "label": "Opcionalidad de salida",
            "body": "Un rol externo conlleva una opcionalidad implícita de 'puedo irme'. En la interna ya has usado una. Infravalorada hasta que la necesitas."
          },
          {
            "label": "Costo psicológico",
            "body": "Explicar la transición repetidamente a los colegas es agotador. La externa es una conversación contigo mismo, y ya está."
          }
        ]
      },
      {
        "kind": "decision-tree",
        "title": "3 ejemplos trabajados",
        "rows": [
          {
            "label": "SWE en Stripe — se queda interno",
            "body": "Stripe tiene un fuerte equipo de infraestructura de ML que publica trabajo real de IA. La credibilidad de las referencias dentro de Stripe es alta para el dominio objetivo (backend → infra de ML es un salto corto). El equipo interno es de primer nivel. Factores que dominan: credibilidad de las referencias + calidad del producto + fortaleza del equipo. Gana la interna."
          },
          {
            "label": "PM en Meta — se va externo",
            "body": "Existen roles de AI PM en Meta pero son políticamente complicados y la organización está en constante reorganización. Su credibilidad de referencias dentro de Meta es por superficie de producto de consumo, no por IA. Las ofertas externas en scale-ups IA-nativas pagan 25% más, dan un alcance más limpio y le permiten construir una nueva reputación desde cero. Factores: calidad del rol + riesgo político + delta de compensación + opcionalidad de salida. Gana la externa."
          },
          {
            "label": "DA en Stitch Fix — se queda interno con fecha límite",
            "body": "Stitch Fix tiene una organización de ML genuina. Su reputación como DA está en realidad en pronóstico de demanda, que es adyacente al ML. Transición interna posible en 3 meses. Transición externa posible en 9 meses. Prueba la interna primero con una fecha límite de 90 días: si no ha progresado a una rotación en un equipo de IA para entonces, se va externa. Factores: tiempo hasta el rol + credibilidad de las referencias (moderada). Estrategia híbrida."
          }
        ]
      },
      {
        "kind": "callout",
        "title": "La regla de intentar primero lo interno",
        "calloutStyle": "insight",
        "body": "Por defecto, intenta primero lo interno, con una fecha límite. Tres meses de esfuerzo interno son baratos (sigues empleado, tu currículum externo no está circulando, tu red no está activada). En la marca de los 3 meses, evalúa con honestidad: ¿has progresado o te han estado dando largas? Si te han dado largas, gira con fuerza hacia lo externo. Correr lo interno + lo externo en paralelo desde el día uno es agotador y a menudo señala desesperación a ambas partes. Lo secuencial le gana a lo paralelo."
      },
      {
        "kind": "checklist",
        "title": "3 señales de alerta — NO intentes primero lo interno",
        "items": [
          "Tu organización actual tiene un patrón documentado de prometer transiciones internas que no suceden (pregunta a tu red: esto suele ser bien conocido)",
          "Se requiere el permiso de tu gerente actual Y tu gerente ha señalado que no apoya la transición",
          "Tu equipo interno objetivo está en un centro de costos / geografía diferente y trasladarte requeriría política interorganizacional que típicamente toma 6+ meses"
        ]
      }
    ]
  }
},
};
